import { AxiosRequestSender } from '@vue-storefront/sdk-axios-request-sender';
import axios from 'axios';

class Config extends Map {
    get(key) {
        return super.get(key);
    }
    set(key, value) {
        super.set(key, value);
        return this;
    }
}
const config = new Config();

/**
 * Resolve base URL based on the client-side or server-side environment.
 * Fallbacks to `apiUrl` when there is no `ssrApiUrl` on server-side environment,
 * bacause `ssrApiUrl` is optional.
 */
const resolveBaseUrl = () => {
    const apiUrl = config.get("apiUrl");
    const ssrApiUrl = config.get("ssrApiUrl");
    const baseUrl = typeof window === "undefined" ? ssrApiUrl || apiUrl : apiUrl;
    return baseUrl;
};

const defaultIsUnauthorized = (error) => {
    return error.response?.status === 401;
};
const defaultRefreshTokenMethod = refreshUserToken;

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * The threshold in milliseconds that will be used to prevent multiple refresh token requests.
 */
const LAST_REFRESH_THRESHOLD = 60000; // 60 seconds
/**
 * The maximum number of retries that will be used to prevent multiple refresh token requests.
 */
const MAX_RETRIES = 1;
/**
 * Create a handler that will handle the refresh token and retry the original request.
 *
 * @example
 * Retry the `getUserAddress` method after the token is refreshed.
 * ```js
 * import { createRefreshTokenAndRetryHandler } from '@vsf-enterprise/sapcc-sdk';
 *
 * const handleRefreshTokenAndRetry = createRefreshTokenAndRetryHandler();
 * const method = sdk.sapcc.getUserAddress;
 * const params = { addressId };
 *
 * try {
 *   const userAddress = await method(params);
 * } catch (error) {
 *   await handleRefreshTokenAndRetry(error, method, [params]);
 * }
 * ```
 *
 * @example
 * Customize the behavior of the `handleRefreshTokenAndRetry` function.
 * ```js
 * import { createRefreshTokenAndRetryHandler } from '@vsf-enterprise/sapcc-sdk';
 *
 * const handleRefreshTokenAndRetry = createRefreshTokenAndRetryHandler();
 * const method = sdk.sapcc.getUserAddress;
 * const params = { addressId };
 * const refreshTokenMethod = sdk.sapcc.refreshUserToken;
 *
 * try {
 *   const userAddress = await method(params);
 * } catch (error) {
 *   await handleRefreshTokenAndRetry(error, method, [params], {
 *     isUnauthorized: (error) => error.response?.status === 401,
 *     refreshTokenMethod,
 *     callbacks: {
 *       onTokenRefreshed: (refreshTokenResponse) => {
 *         console.log('Token refreshed', refreshTokenResponse);
 *       },
 *       onTokenRefreshError: (error) => {
 *         console.log('Error while refreshing the token', error);
 *       },
 *     },
 *   });
 * }
 * ```
 */
function createRefreshTokenAndRetryHandler(initialState) {
    const state = initialState || {
        promise: null,
        lastRefresh: 0,
        retries: 0,
    };
    function resetState() {
        state.lastRefresh = Date.now();
        state.retries = 0;
        state.promise = null;
    }
    /**
     * Handle the refresh token and retry the original request.
     * @param error The error that occurred.
     * @param method The original method that should be retried.
     * @param args The arguments of the original method.
     * @param options Options to customize the behavior of the function.
     */
    return async function handleRefreshTokenAndRetry(error, method, args, options) {
        // Get the options or use the default ones.
        const isUnauthorized = options?.isUnauthorized || defaultIsUnauthorized;
        const refreshTokenMethod = options?.refreshTokenMethod || defaultRefreshTokenMethod;
        const ssrRefreshEnabled = options?.ssrRefreshEnabled || false;
        const isSSR = typeof window === "undefined";
        // If SSR refresh is not enabled during SSR, we should just throw the error.
        if (isSSR && !ssrRefreshEnabled)
            throw error;
        // Refresh token mechanism should be triggered only for unauthorized errors.
        if (!isUnauthorized(error))
            throw error;
        const timeFromLastRefresh = Date.now() - state.lastRefresh;
        const isLastRefreshWithinThreshold = timeFromLastRefresh < LAST_REFRESH_THRESHOLD;
        const canRetry = state.retries < MAX_RETRIES;
        if (isLastRefreshWithinThreshold && canRetry) {
            return method(...args);
        }
        // If there is no refresh token promise, we should create one and store it in the state.
        if (!state.promise) {
            state.promise = refreshTokenMethod()
                .then((response) => {
                options?.callbacks?.onTokenRefreshed(response);
                resetState();
            })
                .catch(async (refreshTokenError) => {
                await options?.callbacks?.onTokenRefreshError(refreshTokenError);
                state.promise = null;
                throw refreshTokenError;
            });
        }
        // Wait for the refresh token promise to finish. Then, clear the promise from the state.
        await state.promise;
        // Retry the original request.
        return method(...args);
    };
}

const clientFactory = (baseUrl) => {
    const axiosClient = axios.create({
        baseURL: baseUrl,
        withCredentials: true,
    });
    const initialState = {
        promise: null,
        lastRefresh: 0,
        retries: 0,
    };
    const handleRefreshTokenAndRetry = createRefreshTokenAndRetryHandler(initialState);
    axiosClient.interceptors.response.use((response) => response, async (error) => {
        const originalRequest = error.config;
        const refreshTokenOptions = config.get("refreshToken");
        return handleRefreshTokenAndRetry(error, axiosClient, [originalRequest], refreshTokenOptions);
    });
    /**
     * This is a proxy that will be used to create an axios instance.
     * It will be used to set the base url and withCredentials flag.
     * Depending on the client-side or server side usage, the base url will be either the `apiUrl` or the `ssrApiUrl`.
     * `resolveBaseUrl` is taking care of that.
     */
    return new Proxy(axiosClient, {
        get: (axiosInstance, property) => {
            axiosInstance.defaults.baseURL = resolveBaseUrl();
            return axiosInstance[property];
        },
    });
};
const client = clientFactory();

/**
 * Method fetching details of a single product from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a GET request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getProduct | getProduct}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Products/getProduct | getProduct}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Products | Products Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Product.
 *
 * @example
 * Fetching a product by its code
 * (passed as the {@link @vsf-enterprise/sapcc-types#ProductGetProps.id | id} parameter).
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const product = await sdk.commerce.getProduct({ id: '1992695' });
 * ```
 *
 * @example
 * Using the method the way it will be possible to cache the response.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const product = await sdk.commerce.getProduct({ id: '1992695', lang: 'en', currency: 'GBP' });
 * ```
 *
 * @example
 * Fetching a product and selecting response {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const product = await sdk.commerce.getProduct({ id: '1992695', fields: 'BASIC' });
 * ```
 */
async function getProduct(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getProduct")
        .setMethod("GET")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method fetching a list of product references from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a GET request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getProductReferences | getProductReferences}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Products/getProductReferences | getProductReferences}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Products | Products Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Product Reference List.
 *
 * @example
 * Fetching references for a product with a specific {@link @vsf-enterprise/sapcc-types#GetProductReferencesProps.productCode | productCode}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const productReferences = await sdk.commerce.getProductReferences({ productCode: '1992695' });
 * ```
 *
 * @example
 * Using the method the way it will be possible to cache the response.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const productReferences = await sdk.commerce.getProductReferences({ productCode: '1992695', lang: 'en', currency: 'GBP' });
 * ```
 *
 * @example
 * Fetching a limited number of references for a product using the {@link @vsf-enterprise/sapcc-types#GetProductReferencesProps.pageSize | pageSize} parameter.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const productReferences = await sdk.commerce.getProductReferences({ productCode: '1992695', pageSize: 2 });
 * ```
 *
 * @example
 * Fetching references of a specific type using the
 * {@link @vsf-enterprise/sapcc-types#GetProductReferencesProps.referenceType | referenceType}
 * parameter. Check {@link @vsf-enterprise/sapcc-types#ProductReferenceTypeEnum | ProductReferenceTypeEnum}
 * for possible values.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const productReferences = await sdk.commerce.getProductReferences({ productCode: '1992695', referenceType: ProductReferenceTypeEnum.ACCESSORIES });
 * ```
 *
 * @example
 * Fetching references for a specific product and selecting response {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const productReferences = await sdk.commerce.getProductReferences({ productCode: '1992695', fields: 'references(description,target(FULL))' });
 * ```
 */
async function getProductReferences(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getProductReferences")
        .setMethod("GET")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method fetching reviews of a single product from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a GET request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getProductReviews | getProductReviews}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Products/getProductReviews | getProductReviews}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Products | Products Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @example
 * Fetching a product by its {@link @vsf-enterprise/sapcc-types#GetProductReviewsProps.productCode | productCode}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const product = await sdk.commerce.getProductReviews({ productCode: '1992695' });
 * ```
 *
 * @example
 * Using the method the way it will be possible to cache the response.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const product = await sdk.commerce.getProductReviews({ productCode: '1992695', lang: 'en', currency: 'GBP' });
 * ```
 *
 * @example
 * Fetching a product by its {@link @vsf-enterprise/sapcc-types#GetProductReviewsProps.productCode | productCode} and selecting response {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const product = await sdk.commerce.getProductReviews({ productCode: '1992695', fields: 'BASIC' });
 * ```
 *
 *  * @example
 * Fetching a product by its {@link @vsf-enterprise/sapcc-types#GetProductReviewsProps.productCode | productCode} and setting the {@link @vsf-enterprise/sapcc-types#GetProductReviewsProps.maxCount | maxCount} of reviews.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const product = await sdk.commerce.getProductReviews({ productCode: '1992695', maxCount: 4 });
 * ```
 */
async function getProductReviews(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getProductReviews")
        .setMethod("GET")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method fetching a list of available search suggestions from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a GET request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getSuggestions | getSuggestions}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Products/getSuggestions | getSuggestions}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Products | Products Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Suggestion List.
 *
 * @example
 * Fetching suggestions for a specific
 * {@link @vsf-enterprise/sapcc-types#SearchSuggestionsProps.searchTerm | searchTerm}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const suggestions = await sdk.commerce.getSearchSuggestions({ searchTerm: 'shirt' });
 * ```
 *
 * @example
 * Using the method the way it will be possible to cache the response.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const suggestions = await sdk.commerce.getSearchSuggestions({ searchTerm: 'shirt', lang: 'en', currency: 'GBP' });
 * ```
 *
 * @example
 * Limiting the number of search suggestions by using the
 * {@link @vsf-enterprise/sapcc-types#SearchSuggestionsProps.max | max} parameter.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const suggestions = await sdk.commerce.getSearchSuggestions({ searchTerm: 'shirt', max: 3 });
 * ```
 */
async function getSearchSuggestions(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getSuggestions")
        .setMethod("GET")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method creating a new customer review as an anonymous user in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.createProductReview | createProductReview}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Products/createProductReview | createProductReview}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Products | Products Controller}.
 *
 * @param {CreateProductReviewProps} props
 * Properties for creating a product review.
 * Refer to its type definition to learn about possible properties.
 *
 * @returns
 * Returns a representation of a Review.
 *
 * @example
 * Creating a new product
 * {@link @vsf-enterprise/sapcc-types#CreateProductReviewProps.review | review}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const review = {
 *   comment: 'This is a great product! Highly recommend it.',
 *   rating: 5,
 *   alias: 'Happy Customer'
 * };
 *
 * const product = await sdk.commerce.createProductReview({ productCode: '12345', review });
 * ```
 *
 * @example
 * Creating a new product
 * {@link @vsf-enterprise/sapcc-types#CreateProductReviewProps.review | review}
 * and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const review = {
 *   comment: 'This is a great product! Highly recommend it.',
 *   rating: 5,
 *   alias: 'Happy Customer'
 * };
 *
 * const product = await sdk.commerce.createProductReview({ productCode: '12345', review, fields: 'BASIC' });
 * ```
 */
async function createProductReview(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("createProductReview")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method fetching a list of products and additional data from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a GET request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.searchProduct | searchProduct}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Products/getProducts | getProducts}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Products | Products Controller}.
 *
 * In the examples below, you can see how various parameter configurations
 * passed to the SDK method affect the final query parameter sent to the
 * SAP OCC API.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Product Search Page. That includes products and additional data,
 * such as available facets, sorting and pagination options. It can also include spelling suggestions.
 *
 * @example
 * Using the method without any parameters to fetch unfiltered
 * products and additional data.
 *
 * Final query: `:undefined`
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const result = await sdk.commerce.searchProduct();
 * ```
 *
 * @example
 * Using the method the way it will be possible to cache the response.
 *
 * Final query: `:undefined`
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const result = await sdk.commerce.searchProduct({ lang: 'en', currency: 'GBP' });
 * ```
 *
 * @example
 * Fetching paginated products and additional data
 * by using the {@link @vsf-enterprise/sapcc-types#ProductSearchProps.pageSize | pageSize}
 * and {@link @vsf-enterprise/sapcc-types#ProductSearchProps.currentPage | currentPage} parameters.
 *
 * Final query: `:undefined`
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const result = await sdk.commerce.searchProduct({ pageSize: 10, currentPage: 1 });
 * ```
 *
 * @example
 * Performing a free-text search and sorting the results by using
 * the {@link @vsf-enterprise/sapcc-types#ProductSearchProps.searchTerm | searchTerm}
 * and {@link @vsf-enterprise/sapcc-types#ProductSearchProps.sort | sort} parameters.
 *
 * Final query: `sneakers:topRated`
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const result = await sdk.commerce.searchProduct({ searchTerm: 'sneakers', sort: 'topRated' });
 * ```
 *
 * @example
 * Performing a free-text search and selecting response fields by using
 * the {@link @vsf-enterprise/sapcc-types#ProductSearchProps.searchTerm | searchTerm}
 * and {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields} parameters. The response object will
 * only contain the freeTextSearch key (alongside type and pagination
 * which are always included - regardless of specified
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}).
 *
 * Final query: `sneakers:undefined`
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const result = await sdk.commerce.searchProduct({ searchTerm: 'sneakers', fields: 'freeTextSearch' });
 * ```
 *
 * @example
 * Using {@link @vsf-enterprise/sapcc-types#ProductSearchProps.filters | filters}
 * parameter to fetch products belonging to specific categories
 * and with a specific average rating. You can view available filters (Indexed Types)
 * and their values in the Solr Facet Search Configuration section in your SAP Backoffice.
 *
 * Final query: `:undefined:allCategories:collections:allCategories:brands:reviewAvgRating:4.4`
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const result = await sdk.commerce.searchProduct({
 *   filters: {
 *     allCategories: ['collections', 'brands'],
 *     reviewAvgRating: [4.4]
 *   }
 * });
 * ```
 *
 * @example
 * Fetching the same subset of products as in the previous example, this time
 * using the {@link @vsf-enterprise/sapcc-types#ProductSearchProps.query | query} parameter.
 *
 * Final query: `:undefined:allCategories:collections:allCategories:brands:reviewAvgRating:4.4`
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const result = await sdk.commerce.searchProduct({ query: ':undefined:allCategories:collections:allCategories:brands:reviewAvgRating:4.4' });
 * ```
 */
async function searchProduct(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("searchProduct")
        .setMethod("GET")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method fetching information about a specific category from SAP Commerce Cloud.
 *
 * @remarks
 * This method send a GET request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getCategory | getCategory}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Catalogs/getCategories | getCategories}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Catalogs | Catalogs Controller}.
 *
 * You can use this method to fetch information about a category available
 * in the Catalog Version used by the current Base Store.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Category Hierarchy.
 *
 * @example
 * Fetching a category by {@link @vsf-enterprise/sapcc-types#GetCategoryProps.id | id}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const category = await sdk.commerce.getCategory({ id: 'collections' });
 * ```
 *
 * @example
 * Using the method the way it will be possible to cache the response.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const category = await sdk.commerce.getCategory({ id: 'collections', lang: 'en', currency: 'GBP' });
 * ```
 *
 * @example
 * Fetching a category by {@link @vsf-enterprise/sapcc-types#GetCategoryProps.id | id}
 * and selecting response {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const category = await sdk.commerce.getCategory({ id: 'collections', fields: 'id,name,url' });
 * ```
 */
async function getCategory(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getCategory")
        .setMethod("GET")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method fetching a specific Catalog Version that
 * exists in the current Base Store in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a GET request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getCatalogVersion | getCatalogVersion}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Catalogs/getCatalogVersion | getCatalogVersion}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Catalogs | Catalogs Controller}.
 *
 * This method does not accept arguments such as `catalogVersionId` or `catalogId`.
 * The API Middleware derives them automatically from the `sapcc` configuration
 * in `middleware.config.js` before sending the request to OCC API. Thus, the method
 * only allows fetching the currently used Catalog Version.
 *
 * The {@link @vsf-enterprise/sap-commerce-webservices-sdk#CatalogVersion | returned object}
 * contains (among others) information about category hierarchies defined for the Catalog Version.
 * It can be used to build navigation components.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Catalog Version.
 *
 * @example
 * Fetching a {@link @vsf-enterprise/sap-commerce-webservices-sdk#CatalogVersion | CatalogVersion}
 * and extracting {@link @vsf-enterprise/sap-commerce-webservices-sdk#CatalogVersion.categories | categories}
 * from it.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const { categories = [] } = await sdk.commerce.getCatalogVersion();
 * ```
 *
 * @example
 * Using the method the way it will be possible to cache the response.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const { categories = [] } = await sdk.commerce.getCatalogVersion({ lang: 'en', currency: 'GBP' });
 * ```
 *
 * @example
 * Fetching a {@link @vsf-enterprise/sap-commerce-webservices-sdk#CatalogVersion | CatalogVersion}
 * with a selected subset of {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const { categories = [] } = await sdk.commerce.getCategory({ fields: 'categories(id,name,url)' });
 * ```
 */
async function getCatalogVersion(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getCatalogVersion")
        .setMethod("GET")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method allowing to fetch combined
 * {@link @vsf-enterprise/sap-commerce-webservices-sdk#ProductSearchPage | product}
 * and {@link @vsf-enterprise/sap-commerce-webservices-sdk#CatalogVersion | catalog}
 * data for product listing pages.
 *
 * @remarks
 * This method sends a GET request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getProductSearchPageData | getProductSearchPageData}
 * endpoint of the Alokai Middleware. It composes responses from two other endpoints:
 * {@link @vsf-enterprise/sapcc-api#Endpoints.searchProduct | searchProduct} and
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getCatalogVersion | getCatalogVersion}.
 * In turn, it receives data from two endpoints exposed by the SAP OCC API:
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Products/getProducts | getProducts}
 * and {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Catalogs/getCatalogVersion | getCatalogVersion}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns composed sources for Product Search results page.
 *
 * @example
 * Using the method without any parameters to fetch
 * {@link @vsf-enterprise/sap-commerce-webservices-sdk#ProductSearchPage | Product Search Page data}
 * (with unfiltered products) and the current
 * {@link @vsf-enterprise/sap-commerce-webservices-sdk#CatalogVersion | Catalog Version}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const searchPageData = await sdk.commerce.getProductSearchPageData();
 * ```
 *
 * @example
 * Using the method the way it will be possible to cache the response.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const searchPageData = await sdk.commerce.getProductSearchPageData({ lang: 'en', currency: 'GBP' });
 * ```
 *
 * @example
 * Using the method with parameters for the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.searchProduct | searchProduct}
 * endpoint. Refer to the {@link @vsf-enterprise/sapcc-types#ProductSearchProps | ProductSearchProps} interface
 * to learn about possible properties.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const searchPageData = await sdk.commerce.getProductSearchPageData({
 *  search: { searchTerm: 'shirt' }
 * });
 * ```
 *
 * @example
 * Using the method with parameters for the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getCatalogVersion | getCatalogVersion}
 * endpoint. Refer to the {@link @vsf-enterprise/sapcc-types#BaseProps | BaseProps} interface
 * to learn about possible properties.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const searchPageData = await sdk.commerce.getProductSearchPageData({
 *  catalog: { fields: 'categories(id,name,url)' }
 * });
 * ```
 *
 * @example
 * Using the method with parameters for both
 * {@link @vsf-enterprise/sapcc-api#Endpoints.searchProduct | searchProduct} and
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getCatalogVersion | getCatalogVersion}
 * endpoints simultaneously.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const searchPageData = await sdk.commerce.getProductSearchPageData({
 *   search: { fields: 'freeTextSearch' },
 *   catalog: { fields: 'categories(id,name,url)' }
 * });
 * ```
 */
async function getProductSearchPageData(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getProductSearchPageData")
        .setMethod("GET")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method fetching available shipping/billing countries from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a GET request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getCountries | getCountries}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Countries/getCountries | getCountries}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Countries | Countries Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a list of countries.
 *
 * @example
 * Calling the method without any params to fetch a list of all available countries.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const category = await sdk.commerce.getCountries();
 * ```
 *
 * @example
 * Using the method the way it will be possible to cache the response.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const category = await sdk.commerce.getCountries({ lang: 'en', currency: 'GBP' });
 * ```
 *
 * @example
 * Fetching a list of all available countries and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const category = await sdk.commerce.getCountries({ fields: 'countries(isocode)' });
 * ```
 *
 * @example
 * Fetching a list of available shipping countries.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const category = await sdk.commerce.getCountries({ type: 'SHIPPING' });
 * ```
 *
 * @example
 * Fetching a list of available billing countries.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const category = await sdk.commerce.getCountries({ type: 'BILLING' });
 * ```
 */
async function getCountries(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getCountries")
        .setMethod("GET")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method fetching a list of available regions for the provided country from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a GET request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getCountryRegions | getCountryRegions}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Countries/getCountryRegions | getCountryRegions}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Countries | Countries Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a list of country regions.
 *
 * @example
 * Fetching a list of available country regions for a specific
 * {@link @vsf-enterprise/sapcc-types#GetCountryRegionsProps.countryIsoCode | countryIsoCode}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const category = await sdk.commerce.getCountryRegions({ countryIsoCode: 'US' });
 * ```
 *
 * @example
 * Using the method the way it will be possible to cache the response.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const category = await sdk.commerce.getCountryRegions({ countryIsoCode: 'US', lang: 'en', currency: 'GBP' });
 * ```
 *
 * @example
 * Fetching a list of available country regions for a specific
 * {@link @vsf-enterprise/sapcc-types#GetCountryRegionsProps.countryIsoCode | countryIsoCode}
 * and selecting response {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const category = await sdk.commerce.getCountryRegions({ countryIsoCode: 'US', fields: 'regions(name,isocode)' });
 * ```
 */
async function getCountryRegions(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getCountryRegions")
        .setMethod("GET")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method fetching a list of all localized titles from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a GET request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getTitles | getTitles}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Miscs/getTitles | getTitles}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Miscs | Miscs Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Title List.
 *
 * @example
 * Fetching a list of all localized titles.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const category = await sdk.commerce.getTitles();
 * ```
 *
 * @example
 * Using the method the way it will be possible to cache the response.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const category = await sdk.commerce.getTitles({ lang: 'en', currency: 'GBP' });
 * ```
 *
 * @example
 * Fetching a list of all localized titles and selecting
 * response {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const category = await sdk.commerce.getTitles({ fields: 'titles(code)' });
 * ```
 */
async function getTitles(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getTitles")
        .setMethod("GET")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method fetching details of a specific Base Store from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a GET request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getBaseStore | getBaseStore}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Base%20Stores/getBaseStore | getBaseStore}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Base%20Stores | Base Stores Controller}.
 *
 * The {@link @vsf-enterprise/sapcc-types#GetBaseStoreProps#baseStoreUid | baseStoreUid}
 * param is optional. If not provided, the method will fall back to either `baseStoreUid`
 * or `baseSiteId` provided in `middleware.config.js`.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Base Store.
 *
 * @example
 * Calling the method without any params and letting it fall back to
 * either `baseStoreUid` or `baseSiteId` provided in `middleware.config.js`
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const category = await sdk.commerce.getBaseStore();
 * ```
 *
 * @example
 * Calling the method with the {@link @vsf-enterprise/sapcc-types#GetBaseStoreProps#baseStoreUid | baseStoreUid}
 * param to explicitly choose the desired Base Store.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const category = await sdk.commerce.getBaseStore({ baseStoreUid: 'electronics' });
 * ```
 *
 * @example
 * Fetching details of a specific Base Store and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const category = await sdk.commerce.getBaseStore({ fields: 'defaultCurrency(isocode),currencies(isocode)' });
 * ```
 *
 */
async function getBaseStore(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getBaseStore")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method creating a new cart or restoring an anonymous cart as a user's cart in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.createCart | createCart}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Carts/createCart | createCart}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Carts | Carts Controller}.
 *
 * When `vsf-sap-token` cookie is present in the browser, this method will create a cart
 * for the current user. In case there is no cookie, it will create a cart for an anonymous
 * user.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Cart.
 *
 * @example
 * Creating a new cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cart = await sdk.commerce.createCart();
 * ```
 *
 * @example
 * Creating a new cart and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cart = await sdk.commerce.createCart({ fields: 'code,guid,user(FULL)' });
 * ```
 *
 * @example
 * Turning an anonymous cart into the current user's cart by passing the
 * anonymous cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * as the {@link @vsf-enterprise/sapcc-types#CreateCartProps.oldCartId | oldCartId}
 * parameter.
 *
 * You have to be logged-in to use this method this way.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cart = await sdk.commerce.createCart({ oldCartId: '1da746dc-aa8f-4914-a70e-49f1b32ca0db' });
 * ```
 *
 * @example
 * Merging an anonymous cart with the current user's cart by passing the
 * anonymous cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * as the {@link @vsf-enterprise/sapcc-types#CreateCartProps.oldCartId | oldCartId}
 * parameter and the user's cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * as the {@link @vsf-enterprise/sapcc-types#CreateCartProps.toMergeCartGuid | toMergeCartGuid}
 * parameter.
 *
 * You have to be logged-in to use this method this way.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cart = await sdk.commerce.createCart({
 *  oldCartId: '1da746dc-aa8f-4914-a70e-49f1b32ca0db',
 *  toMergeCartGuid: 'c2c68786-bb31-4bb4-aa1c-8ad9b43de8ef'
 * });
 * ```
 */
async function createCart(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("createCart")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method fetching a cart with a given identifier from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getCart | getCart}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Carts/getCart | getCart}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Carts | Carts Controller}.
 *
 * When `vsf-sap-token` cookie is present in the browser, this method will fetch
 * a cart for the current user. In case there is no cookie, it will fetch a cart for an anonymous
 * user. For logged-in users, make sure to pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * as the {@link @vsf-enterprise/sapcc-types#GetCartProps.cartId | cartId} param. In case of
 * anonymous users, pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * instead.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Cart.
 *
 * @example
 * Fetching a cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cart = await sdk.commerce.getCart({ cartId: '00035084' });
 * ```
 *
 * @example
 * Fetching a cart and selecting response {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cart = await sdk.commerce.getCart({ cartId: '00035084', fields: 'code,guid,user(FULL)' });
 * ```
 */
async function getCart(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getCart")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method deleting a cart with a given identifier from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.deleteCart | deleteCart}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Carts/removeCart | removeCart}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Carts | Carts Controller}.
 *
 * When `vsf-sap-token` cookie is present in the browser, this method will delete
 * the current user cart. In case there is no cookie, it will delete an anonymous
 * user cart. For logged-in users, make sure to pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * as the {@link @vsf-enterprise/sapcc-types#DeleteCartProps.cartId | cartId} param. In case of
 * anonymous users, pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * instead.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * This method does not return anything.
 *
 * @example
 * Deleting a cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * await sdk.commerce.deleteCart({ cartId: '00035084' });
 * ```
 */
async function deleteCart(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("deleteCart")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method turning an active user cart into a saved cart in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.saveCart | saveCart}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Carts/doSaveCart | doSaveCart}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Save%20Cart | Save Cart Controller}.
 *
 * This method works only in an authenticated user session (i.e. when the `vsf-sap-token` cookie is present in the browser).
 * Also, make sure you are passing cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * (and not {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}) as the
 * {@link @vsf-enterprise/sapcc-types#SaveCartProps.cartId | cartId} param.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Save Cart Result.
 *
 * @example
 * Turning an active user cart into a saved cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const savedCart = await sdk.commerce.saveCart({ cartId: '00035084' });
 * ```
 *
 * @example
 * Turning an active user cart into a saved cart with
 * {@link @vsf-enterprise/sapcc-types#SaveCartProps.name | name}
 * and {@link @vsf-enterprise/sapcc-types#SaveCartProps.description | description}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const savedCart = await sdk.commerce.saveCart({
 *   cartId: '00035084',
 *   name: 'wishlist',
 *   description: 'This is a wishlist description'
 * });
 * ```
 *
 * @example
 * Turning an active user cart into a saved cart and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const savedCart = await sdk.commerce.saveCart({
 *   cartId: '00035084',
 *   fields: 'savedCartData(code,name,savedBy(FULL))'
 * });
 * ```
 */
async function saveCart(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("saveCart")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method cloning a saved user cart in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.cloneCart | cloneCart}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Carts/doCartClone | doCartClone}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Save%20Cart | Save Cart Controller}.
 *
 * This method works only in an authenticated user session (i.e. when the `vsf-sap-token` cookie is present in the browser).
 * Also, make sure you are passing cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * (and not {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}) as the
 * {@link @vsf-enterprise/sapcc-types#SaveCartProps.cartId | cartId} param.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Save Cart Result.
 *
 * @example
 * Cloning a saved user cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const clonedCart = await sdk.commerce.cloneCart({ cartId: '00035084' });
 * ```
 *
 * @example
 * Cloning a saved user cart and giving it
 * {@link @vsf-enterprise/sapcc-types#SaveCartProps.name | name}
 * and {@link @vsf-enterprise/sapcc-types#SaveCartProps.description | description}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const clonedCart = await sdk.commerce.cloneCart({
 *   cartId: '00035084',
 *   name: 'cloned-cart-name',
 *   description: 'This is a cloned cart description'
 * });
 * ```
 *
 * @example
 * Cloning a saved user cart and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const savedCart = await sdk.commerce.cloneCart({
 *   cartId: '00035084',
 *   fields: 'savedCartData(name,code,savedBy(FULL))'
 * });
 * ```
 */
async function cloneCart(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("cloneCart")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method turning a saved user cart into an active user cart in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.restoreCart | restoreCart}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Carts/doUpdateSavedCart | doUpdateSavedCart}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Save%20Cart | Save Cart Controller}.
 *
 * This method works only in an authenticated user session (i.e. when the `vsf-sap-token` cookie is present in the browser).
 * Also, make sure you are passing cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * (and not {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}) as the
 * {@link @vsf-enterprise/sapcc-types#RestoreCartProps.cartId | cartId} param.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Save Cart Result.
 *
 * @example
 * Restoring a saved user cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const restoredCart = await sdk.commerce.restoreCart({ cartId: '00035084' });
 * ```
 *
 * @example
 * Restoring a saved user cart and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ``ts
 * import { sdk } from '~/sdk';
 *
 * const restoredCart = await sdk.commerce.restoreCart({
 *   cartId: '00035084',
 *   fields: 'savedCartData(code,savedBy(FULL),user(FULL))'
 * });
 * ```
 */
async function restoreCart(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("restoreCart")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method adding a product to the cart in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.addToCart | addToCart}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Entries/createCartEntry | createCartEntry}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Entries | Cart Entries Controller}.
 *
 * When `vsf-sap-token` cookie is present in the browser, this method will add a product to the
 * current user's cart. In case there is no cookie, it will add it to the anonymous
 * user's cart. For logged-in users, make sure to pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * as the {@link @vsf-enterprise/sapcc-types#AddToCartProps.cartId | cartId} param. In case of
 * anonymous users, pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * instead.
 *
 * You might also want to see the {@link addEntryAndGetCart | addEntryAndGetCart} method.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Cart Modification.
 *
 * @example
 * Adding product to the cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cartModification = await sdk.commerce.addCartEntry({
 *   cartId: '00035084',
 *   entry: {
 *     quantity: 1,
 *     product: { code: '1992695' }
 *   }
 * });
 * ```
 *
 * @example
 * Adding product to the cart and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cartModification = await sdk.commerce.addCartEntry({
 *   cartId: '00035084',
 *   entry: {
 *     quantity: 1,
 *     product: { code: '1992695' }
 *   },
 *   fields: 'quantity,entry(product(code))'
 * });
 * ```
 *
 * @example
 * Adding product to the cart and setting
 * {@link @vsf-enterprise/sap-commerce-webservices-sdk#OrderEntry.deliveryPointOfService | deliveryPointOfService}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cartModification = await sdk.commerce.addCartEntry({
 *   cartId: '00035084',
 *   entry: {
 *     quantity: 1,
 *     product: { code: '1992695' },
 *     deliveryPointOfService: { name: 'ap_warehouse_e' }
 *   },
 * });
 * ```
 */
async function addCartEntry(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("addToCart")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method adding a product to the cart and getting the updated cart from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.addCartEntry | addCartEntry}
 * endpoint of the Alokai Middleware which composes responses from two other endpoints:
 * {@link @vsf-enterprise/sapcc-api#Endpoints.addToCart | addToCart} and
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getCart | getCart}.
 * In turn, it exchanges data with two endpoints exposed by the SAP OCC API:
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Entries/createCartEntry | createCartEntry}
 * and {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Carts/getCart | getCart}.
 *
 * When `vsf-sap-token` cookie is present in the browser, this method will add a product to the
 * current user's cart. In case there is no cookie, it will add it to the anonymous
 * user's cart. For logged-in users, make sure to pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * as the {@link @vsf-enterprise/sapcc-types#AddToCartProps.cartId | cartId} param. In case of
 * anonymous users, pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * instead.
 *
 * You might also want to see the {@link addCartEntry | addCartEntry} method.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Cart.
 *
 * @example
 * Adding product to the cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const updatedCart = await sdk.commerce.addEntryAndGetCart({
 *   cartId: '00035084',
 *   entry: {
 *     quantity: 1,
 *     product: { code: '1992695' }
 *   }
 * });
 * ```
 *
 * @example
 * Adding product to the cart and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const updatedCart = await sdk.commerce.addEntryAndGetCart({
 *   cartId: '00035084',
 *   entry: {
 *     quantity: 1,
 *     product: { code: '1992695' }
 *   },
 *   fields: 'guid,entries(quantity,product(code))'
 * });
 * ```
 *
 * @example
 * Adding product to the cart and setting
 * {@link @vsf-enterprise/sap-commerce-webservices-sdk#OrderEntry.deliveryPointOfService | deliveryPointOfService}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const updatedCart = await sdk.commerce.addEntryAndGetCart({
 *   cartId: '00035084',
 *   entry: {
 *     quantity: 1,
 *     product: { code: '1992695' },
 *     deliveryPointOfService: { name: 'ap_warehouse_e' }
 *   },
 * });
 * ```
 */
async function addEntryAndGetCart(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("addCartEntry")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method removing a product from the cart in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.deleteFromCart | deleteFromCart}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Entries/removeCartEntry | removeCartEntry}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Entries | Cart Entries Controller}.
 *
 * When `vsf-sap-token` cookie is present in the browser, this method will remove a product from the
 * current user's cart. In case there is no cookie, it will remove it from the anonymous
 * user's cart. For logged-in users, make sure to pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * as the {@link @vsf-enterprise/sapcc-types#DeleteFromCartProps.cartId | cartId} param. In case of
 * anonymous users, pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * instead.
 *
 * You might also want to see the {@link removeEntryAndGetCart | removeEntryAndGetCart} method.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * This method does not return anything.
 *
 * @example
 * Removing product from the cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * await sdk.commerce.removeCartEntry({
 *   cartId: '00035084',
 *   entryNumber: 1
 * });
 * ```
 */
async function removeCartEntry(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("deleteFromCart")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method removing a product from the cart and getting the updated cart from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.deleteCartEntry | deleteCartEntry}
 * endpoint of the Alokai Middleware which composes responses from two other endpoints:
 * {@link @vsf-enterprise/sapcc-api#Endpoints.deleteFromCart | deleteFromCart} and
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getCart | getCart}.
 * In turn, it exchanges data with two endpoints exposed by the SAP OCC API:
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Entries/removeCartEntry | removeCartEntry}
 * and {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Carts/getCart | getCart}.
 *
 * When `vsf-sap-token` cookie is present in the browser, this method will remove a product from the
 * current user's cart. In case there is no cookie, it will remove it from the anonymous
 * user's cart. For logged-in users, make sure to pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * as the {@link @vsf-enterprise/sapcc-types#DeleteFromCartProps.cartId | cartId} param. In case of
 * anonymous users, pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * instead.
 *
 * You might also want to see the {@link removeCartEntry | removeCartEntry} method.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Cart.
 *
 * @example
 * Removing product from the cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const updatedCart = await sdk.commerce.removeEntryAndGetCart({
 *   cartId: '00035084',
 *   entryNumber: 1
 * });
 * ```
 *
 * @example
 * Removing product from the cart and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const updatedCart = await sdk.commerce.removeEntryAndGetCart({
 *   cartId: '00035084',
 *   entryNumber: 1,
 *   fields: 'guid,entries(quantity,product(code))'
 * });
 * ```
 */
async function removeEntryAndGetCart(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("deleteCartEntry")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method updating a cart entry (its quantity, store details) in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.updateCart | updateCart}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Entries/updateCartEntry | updateCartEntry}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Entries | Cart Entries Controller}.
 *
 * When `vsf-sap-token` cookie is present in the browser, this method will update an entry of the
 * current user's cart. In case there is no cookie, it will update it in the anonymous
 * user's cart. For logged-in users, make sure to pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * as the {@link @vsf-enterprise/sapcc-types#UpdateCartProps.cartId | cartId} param. In case of
 * anonymous users, pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * instead.
 *
 * You might also want to see the {@link updateEntryAndGetCart | updateEntryAndGetCart} method.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Cart Modification.
 *
 * @example
 * Updating {@link @vsf-enterprise/sap-commerce-webservices-sdk#OrderEntry.quantity | quantity}
 * of a cart entry with a given
 * {@link @vsf-enterprise/sapcc-types#UpdateCartProps.entryNumber | entryNumber}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cartModification = await sdk.commerce.updateCartEntry({
 *   cartId: '00035084',
 *   entryNumber: 0,
 *   entry: { quantity: 2 }
 * });
 * ```
 *
 * @example
 * Updating {@link @vsf-enterprise/sap-commerce-webservices-sdk#OrderEntry.quantity | quantity}
 * of a cart entry and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cartModification = await sdk.commerce.updateCartEntry({
 *   cartId: '00035084',
 *   entryNumber: 0,
 *   entry: { quantity: 2 },
 *   fields: 'quantityAdded,statusCode'
 * });
 * ```
 *
 * @example
 * Updating {@link @vsf-enterprise/sap-commerce-webservices-sdk#OrderEntry.quantity | quantity} and
 * {@link @vsf-enterprise/sap-commerce-webservices-sdk#OrderEntry.deliveryPointOfService | deliveryPointOfService}
 * of a cart entry.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cartModification = await sdk.commerce.updateCartEntry({
 *   cartId: '00035084',
 *   entryNumber: 0,
 *   entry: {
 *     quantity: 2,
 *     deliveryPointOfService: { name: 'ap_warehouse_e' }
 *   }
 * });
 * ```
 */
async function updateCartEntry(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("updateCart")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method updating a cart entry (its quantity, store details) and getting the updated cart from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.updateCartEntry | updateCartEntry}
 * endpoint of the Alokai Middleware which composes responses from two other endpoints:
 * {@link @vsf-enterprise/sapcc-api#Endpoints.updateCart | updateCart} and
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getCart | getCart}.
 * In turn, it exchanges data with two endpoints exposed by the SAP OCC API:
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Entries/updateCartEntry | updateCartEntry}
 * and {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Carts/getCart | getCart}.
 *
 * When `vsf-sap-token` cookie is present in the browser, this method will update an entry of the
 * current user's cart. In case there is no cookie, it will update it in the anonymous
 * user's cart. For logged-in users, make sure to pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * as the {@link @vsf-enterprise/sapcc-types#GetCartProps.cartId | cartId} param. In case of
 * anonymous users, pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * instead.
 *
 * You might also want to see the {@link updateCartEntry | updateCartEntry} method.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Cart.
 *
 * @example
 * Updating {@link @vsf-enterprise/sap-commerce-webservices-sdk#OrderEntry.quantity | quantity}
 * of a cart entry with a given
 * {@link @vsf-enterprise/sapcc-types#UpdateCartProps.entryNumber | entryNumber}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const updatedCart = await sdk.commerce.updateEntryAndGetCart({
 *   cartId: '00035084',
 *   entryNumber: 0,
 *   entry: { quantity: 2 }
 * });
 * ```
 *
 * @example
 * Updating {@link @vsf-enterprise/sap-commerce-webservices-sdk#OrderEntry.quantity | quantity}
 * of a cart entry and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const updatedCart = await sdk.commerce.updateEntryAndGetCart({
 *   cartId: '00035084',
 *   entryNumber: 0,
 *   entry: { quantity: 2 },
 *   fields: 'guid,entries(quantity,product(code))'
 * });
 * ```
 *
 * @example
 * Updating {@link @vsf-enterprise/sap-commerce-webservices-sdk#OrderEntry.quantity | quantity} and
 * {@link @vsf-enterprise/sap-commerce-webservices-sdk#OrderEntry.deliveryPointOfService | deliveryPointOfService}
 * of a cart entry.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const updatedCart = await sdk.commerce.updateEntryAndGetCart({
 *   cartId: '00035084',
 *   entryNumber: 0,
 *   entry: {
 *     quantity: 2,
 *     deliveryPointOfService: { name: 'ap_warehouse_e' }
 *   }
 * });
 * ```
 */
async function updateEntryAndGetCart(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("updateCartEntry")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method assigning an email to the cart and initializing guest checkout in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.addGuestEmailToCart | addGuestEmailToCart}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Carts/replaceCartGuestUser | replaceCartGuestUser}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Carts | Carts Controller}.
 *
 * In SAP Commerce Cloud, the sole purpose of assigning an email address to the cart is to initialize a guest checkout.
 * Therefore, the `addCartEmail()` method works with anonymous carts only. It will not work when `vsf-sap-token` cookie
 * is present in the browser.
 *
 * Also, remember to pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * as the {@link @vsf-enterprise/sapcc-types#AddGuestEmailToCartProps.cartId | cartId} param.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * This method does not return anything.
 *
 * @example
 * Assigning email to the cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cartModification = await sdk.commerce.addCartEmail({
 *   cartId: '00035084',
 *   email: 'test@gmail.com'
 * });
 * ```
 */
async function addCartEmail(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("addGuestEmailToCart")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method creating an address and assigning it to the cart as delivery address in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.createCartAddress | createCartAddress}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Addresses/createCartDeliveryAddress | createCartDeliveryAddress}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Addresses | Cart Addresses Controller}.
 *
 * This method will only work for either a current user cart or a guest cart (i.e. cart with an
 * email {@link addCartEmail | added} to it). It will not work with an anonymous cart. In case
 * of a guest cart, remember to pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * as the {@link @vsf-enterprise/sapcc-types#CreateCartAddressProps.cartId | cartId} param.
 * In case of a current user cart, pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * instead.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of an Address.
 *
 * @example
 * Creating a delivery address for a cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cartAddress = await sdk.commerce.createCartAddress({
 *   cartId: '00035084',
 *   address: {
 *     titleCode: 'mr',
 *     firstName: 'John',
 *     lastName: 'Doe',
 *     line1: 'Some Street',
 *     postalCode: '54-022',
 *     country: { isocode: 'GB' },
 *     town: 'Radom'
 *   }
 * });
 * ```
 *
 * @example
 * Creating a delivery address for a cart and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cartAddress = await sdk.commerce.createCartAddress({
 *   cartId: '00035084',
 *   address: {
 *     titleCode: 'mr',
 *     firstName: 'John',
 *     lastName: 'Doe',
 *     line1: 'Some Street',
 *     postalCode: '54-022',
 *     country: { isocode: 'GB' },
 *     town: 'Radom'
 *   },
 *   fields: 'firstName,lastName,town'
 * });
 * ```
 *
 */
async function createCartAddress(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("createCartAddress")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method removing a delivery address from the cart in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.removeCartAddress | removeCartAddress}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Addresses/removeCartDeliveryAddress | removeCartDeliveryAddress}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Addresses | Cart Addresses Controller}.
 *
 * This method will only work for either a current user cart or a guest cart (i.e. cart with an
 * email {@link addCartEmail | added} to it). It will not work with an anonymous cart. In case
 * of a guest cart, remember to pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * as the {@link @vsf-enterprise/sapcc-types#RemoveCartAddressProps.cartId | cartId} param.
 * In case of a current user cart, pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * instead.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * This method does not return anything.
 *
 * @example
 * Removing a delivery address from the cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * await sdk.commerce.removeCartAddress({ cartId: '00035084' });
 * ```
 *
 */
async function removeCartAddress(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("removeCartAddress")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method assigning an existing delivery address to the cart in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.replaceCartAddress | replaceCartAddress}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Addresses/replaceCartDeliveryAddress | replaceCartDeliveryAddress}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Addresses | Cart Addresses Controller}.
 *
 * This method will only work for either a current user cart or a guest cart (i.e. cart with an
 * email {@link addCartEmail | added} to it). It will not work with an anonymous cart. In case
 * of a guest cart, remember to pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * as the {@link @vsf-enterprise/sapcc-types#ReplaceCartAddressProps.cartId | cartId} param.
 * In case of a current user cart, pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * instead.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * This method does not return anything.
 *
 * @example
 * Assigning an existing delivery address to the cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * await sdk.commerce.replaceCartAddress({ cartId: '00035084', addressId: '8796716793879' });
 * ```
 *
 */
async function replaceCartAddress(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("replaceCartAddress")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method getting available delivery modes for the cart in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getCartDeliveryModes | getCartDeliveryModes}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Delivery%20Modes/getCartDeliveryModes | getCartDeliveryModes}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Delivery%20Modes | Cart Delivery Modes Controller}.
 *
 * This method will only work for either a current user cart or a guest cart (i.e. cart with an
 * email {@link addCartEmail | added} to it). It will not work with an anonymous cart.
 * The cart has to be assigned a delivery address beforehand. In case
 * of a guest cart, remember to pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * as the {@link @vsf-enterprise/sapcc-types#GetCartDeliveryModesProps.cartId | cartId} param.
 * In case of a current user cart, pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * instead.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Delivery Mode List.
 *
 * @example
 * Fetching delivery modes for the cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const { deliveryModes } = await sdk.commerce.getCartDeliveryModes({ cartId: '00035084' });
 * ```
 *
 * @example
 * Fetching delivery modes for the cart and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const { deliveryModes } = await sdk.commerce.getCartDeliveryModes({
 *   cartId: '00035084',
 *   fields: 'deliveryModes(code)'
 * });
 * ```
 *
 */
async function getCartDeliveryModes(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getCartDeliveryModes")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method setting a delivery mode for the cart in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.replaceCartDeliveryMode | replaceCartDeliveryMode}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Delivery%20Modes/replaceCartDeliveryMode | replaceCartDeliveryMode}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Delivery%20Modes | Cart Delivery Modes Controller}.
 *
 * This method will only work for either a current user cart or a guest cart (i.e. cart with an
 * email {@link addCartEmail | added} to it). It will not work with an anonymous cart.
 * The cart has to be assigned a delivery address beforehand. In case
 * of a guest cart, remember to pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * as the {@link @vsf-enterprise/sapcc-types#ReplaceCartDeliveryModeProps.cartId | cartId} param.
 * In case of a current user cart, pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * instead.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * This method does not return anything.
 *
 * @example
 * Setting delivery mode for a cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * await sdk.commerce.replaceCartDeliveryMode({ cartId: '00035084', deliveryModeId: 'premium-gross' });
 * ```
 *
 */
async function replaceCartDeliveryMode(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("replaceCartDeliveryMode")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method creating payment details and assigning them to the cart in SAP Commerce Cloud.
 *
 * @internal
 * **This method allows for placing orders in SAP Commerce Cloud without using a proper payment provider.
 * It is suited only to demos and local development. In production, it should be replaced with a proper
 * payment provider action responsible for processing customers' sensitive credit card details.**
 *
 * @remarks
 *
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.createCartPaymentDetails | createCartPaymentDetails}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Payments/createCartPaymentDetails | createCartPaymentDetails}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Payments | Cart Payments Controller}.
 *
 * This method will only work for either a current user cart or a guest cart (i.e. cart with an
 * email {@link addCartEmail | added} to it). It will not work with an anonymous cart. In case
 * of a guest cart, remember to pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * as the {@link @vsf-enterprise/sapcc-types#CreateCartPaymentDetailsProps.cartId | cartId} param.
 * In case of a current user cart, pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * instead.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of Payment Details.
 *
 * @example
 * Creating and assigning payment details to a cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const paymentDetails = {
 *   accountHolderName: 'John Doe',
 *   cardNumber: '4111111111111111',
 *   cardType: { code: 'maestro', name: 'Maestro' },
 *   expiryMonth: '02',
 *   expiryYear: '2024',
 *   billingAddress: {
 *     titleCode: 'Mr',
 *     firstName: 'John',
 *     lastName: 'Doe',
 *     line1: 'Lineone',
 *     postalCode: '54-025',
 *     town: 'Townish',
 *     country: { isocode: 'GB' }
 *   }
 * };
 *
 * const paymentDetails = await sdk.commerce.createCartPaymentDetails({
 *   cartId: '00035084',
 *   paymentDetails
 * });
 * ```
 *
 * @example
 * Creating and assigning payment details to a cart and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const paymentDetails = {
 *   accountHolderName: 'John Doe',
 *   cardNumber: '4111111111111111',
 *   cardType: { code: 'maestro', name: 'Maestro' },
 *   expiryMonth: '02',
 *   expiryYear: '2024',
 *   billingAddress: {
 *     titleCode: 'Mr',
 *     firstName: 'John',
 *     lastName: 'Doe',
 *     line1: 'Lineone',
 *     postalCode: '54-025',
 *     town: 'Townish',
 *     country: { isocode: 'GB' }
 *   }
 * };
 *
 * const paymentDetails = await sdk.commerce.createCartPaymentDetails({
 *   cartId: '00035084',
 *   paymentDetails,
 *   fields: 'accountHolderName,expiryMonth,expiryYear'
 * });
 * ```
 *
 */
async function createCartPaymentDetails(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("createCartPaymentDetails")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method assigning payment details to the cart in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.replaceCartPaymentDetails | replaceCartPaymentDetails}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Payments/replaceCartPaymentDetails | replaceCartPaymentDetails}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Payments | Cart Payments Controller}.
 *
 * This method will only work for either a current user cart or a guest cart (i.e. cart with an
 * email {@link addCartEmail | added} to it). It will not work with an anonymous cart. In case
 * of a guest cart, remember to pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * as the {@link @vsf-enterprise/sapcc-types#ReplaceCartPaymentDetailsProps.cartId | cartId} param.
 * In case of a current user cart, pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * instead.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * This method does not return anything.
 *
 * @example
 * Assigning payment details to a cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * await sdk.commerce.createCartPaymentDetails({ cartId: '00035084', paymentDetailsId: '8796814737431' });
 * ```
 */
async function replaceCartPaymentDetails(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("replaceCartPaymentDetails")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method adding voucher to the cart in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.addVoucherToCart | addVoucherToCart}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Promotions/doApplyCartVoucher | doApplyCartVoucher}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Promotions | Cart Promotions Controller}.
 *
 * This method will work for all types of carts (anonymous, user and guest). In case
 * of guest and anonymous carts, remember to pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * as the {@link @vsf-enterprise/sapcc-types#CreateCartPaymentDetailsProps.cartId | cartId} param.
 * In case of a current user cart, pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * instead.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * This method does not return anything.
 *
 * @example
 * Adding voucher to the cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * await sdk.commerce.addCartVoucher({ cartId: '00035084', voucherId: 'springfestival' });
 * ```
 */
async function addCartVoucher(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("addVoucherToCart")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method adding a voucher to the cart and getting the updated cart from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.addVoucherAndGetNewCartVersion | addVoucherAndGetNewCartVersion}
 * endpoint of the Alokai Middleware which composes responses from two other endpoints:
 * {@link @vsf-enterprise/sapcc-api#Endpoints.addToCart | addVoucherToCart} and
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getCart | getCart}.
 * In turn, it exchanges data with two endpoints exposed by the SAP OCC API:
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Entries/doApplyCartVoucher | doApplyCartVoucher}
 * and {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Carts/getCart | getCart}.
 *
 * When `vsf-sap-token` cookie is present in the browser, this method will add a voucher to the
 * current user's cart. In case there is no cookie, it will add it to the anonymous
 * user's cart. For logged-in users, make sure to pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * as the {@link @vsf-enterprise/sapcc-types#AddVoucherAndGetNewCartVersionProps.cartId | cartId} param. In case of
 * anonymous users, pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * instead.
 *
 * You might also want to see the {@link addCartVoucher | addCartVoucher} method.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Cart.
 *
 * @example
 * Adding a voucher to the cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const updatedCart = await sdk.commerce.addVoucherAndGetCart({
 *   cartId: '00035084',
 *   voucherId: 'WINTER16'
 * });
 * ```
 *
 * @example
 * Adding a voucher to the cart and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const updatedCart = await sdk.commerce.addVoucherAndGetCart({
 *   cartId: '00035084',
 *   voucherId: 'WINTER16',
 *   fields: 'guid,entries(quantity,product(code))'
 * });
 * ```
 */
async function addVoucherAndGetCart(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("addVoucherAndGetNewCartVersion")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method removing a voucher from the cart in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.removeVoucherFromCart | removeVoucherFromCart}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Promotions/removeCartVoucher | removeCartVoucher}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Cart%20Promotions | Cart Promotions Controller}.
 *
 * This method will work for all types of carts (anonymous, user and guest). In case
 * of guest and anonymous carts, remember to pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * as the {@link @vsf-enterprise/sapcc-types#CreateCartPaymentDetailsProps.cartId | cartId} param.
 * In case of a current user cart, pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * instead.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * This method does not return anything.
 *
 * @example
 * Removing voucher from the cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * await sdk.commerce.removeCartVoucher({ cartId: '00035084', voucherId: 'springfestival' });
 * ```
 */
async function removeCartVoucher(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("removeVoucherFromCart")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method authorizing a cart and placing an order in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.placeOrder | placeOrder}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Orders/placeOrder | placeOrder}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Orders | Orders Controller}.
 *
 * This method will only work for either a current user cart or a guest cart (i.e. cart with an
 * email {@link addCartEmail | added} to it). It will not work with an anonymous cart. In case
 * of a guest cart, remember to pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.guid | guid}
 * as the {@link @vsf-enterprise/sapcc-types#PlaceOrderProps.cartId | cartId} param.
 * In case of a current user cart, pass cart {@link @vsf-enterprise/sap-commerce-webservices-sdk#Cart.code | code}
 * instead.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of an Order.
 *
 * @example
 * Placing an order from a cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const order = await sdk.commerce.placeOrder({ cartId: '00035084' });
 * ```
 *
 * @example
 * Placing an order from a cart and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const order = await sdk.commerce.placeOrder({ cartId: '00035084', fields: 'code,guid,deliveryStatus' });
 * ```
 *
 */
async function placeOrder(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("placeOrder")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method fetching an order from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getUserOrders | getUserOrders}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Orders/getUserOrders | getUserOrders}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Orders | Orders Controller}.
 *
 * For logged-in users, make sure to pass order {@link @vsf-enterprise/sap-commerce-webservices-sdk#Order.code | code}
 * as the {@link @vsf-enterprise/sapcc-types#GetUserOrdersProps.code | code} param. In case of
 * anonymous users, pass order {@link @vsf-enterprise/sap-commerce-webservices-sdk#Order.guid | guid}
 * instead.
 *
 * You might also want to see the {@link getUserOrderHistory | getUserOrderHistory} method.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of an Order.
 *
 * @example
 * Fetching an order.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const order = await sdk.commerce.getUserOrder({ code: '00035084' });
 * ```
 *
 * @example
 * Fetching an order and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const order = await sdk.commerce.getUserOrder({ code: '00035084', fields: 'code,guid,deliveryStatus' });
 * ```
 *
 */
async function getUserOrder(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getUserOrders")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method registering a new user in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.createUser | createUser}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Users/createUser | createUser}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Users | Users Controller}.
 *
 * This method works only in an anonymous user session (i.e. when the `vsf-sap-token` cookie is not present in the browser).
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a User.
 *
 * @example
 * Creating a new user.
 *
 * ```ts
 * import { UserSignUpProps } from '@vsf-enterprise/sapcc-types';
 * import { sdk } from '~/sdk';
 *
 * const props: UserSignUpProps = {
 *   titleCode: 'mr',
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   password: 'Test1234!',
 *   uid: 'test@gmail.com'
 * }
 *
 * const user = await sdk.commerce.createUser(props);
 * ```
 *
 * @example
 * Creating a new user and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { UserSignUpProps } from '@vsf-enterprise/sapcc-types';
 * import { sdk } from '~/sdk';
 *
 * const props: UserSignUpProps = {
 *   titleCode: 'mr',
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   password: 'Test1234!',
 *   uid: 'test@gmail.com',
 *   fields: 'uid,firstName'
 * }
 *
 * const user = await sdk.commerce.createUser(props);
 * ```
 */
async function createUser(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("createUser")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method fetching a customer profile from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getUser | getUser}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Users/getUser | getUser}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Users | Users Controller}.
 *
 * The method fetches a customer profile based on the `vsf-sap-token` cookie (which is assigned to that customer).
 * The cookie must be present in the browser for the method to work properly.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a User.
 *
 * @example
 * Fetching a customer profile.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const user = await sdk.commerce.getUser();
 * ```
 *
 * @example
 * Fetching a customer profile and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const user = await sdk.commerce.getUser({ fields: 'uid,firstName' });
 * ```
 */
async function getUser(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getUser")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method initializing an authenticated user session in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.OAuthUserAuthorization | OAuthUserAuthorization}
 * endpoint of the Alokai Middleware. In turn, it retrieves an access token from
 * the {@link @vsf-enterprise/sapcc-api#MiddlewareOAuthConfig#tokenEndpoint | token endpoint}
 * of the {@link @vsf-enterprise/sapcc-api#MiddlewareOAuthConfig | SAP OAuth server}.
 * The token is saved in the browser as the `vsf-sap-token` cookie automatically using the
 * {@link https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Set-Cookie | Set-Cookie}
 * response header. The default options of the cookie can be modified through the
 * {@link @vsf-enterprise/sapcc-api#MiddlewareOAuthConfig#cookieOptions | cookieOptions}
 * property in `middleware.config.js`.
 *
 * You might also want to see the {@link signUserOut | signUserOut} method.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of an OAuthUserTokenResponse.
 *
 * @example
 * Signing a user in.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const tokenResponse = await sdk.commerce.signUserIn({
 *   username: 'test@gmail.com',
 *   password: 'test123!'
 * });
 * ```
 */
async function signUserIn(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("OAuthUserAuthorization")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method terminating an authenticated user session in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.OAuthUserRevoke | OAuthUserRevoke}
 * endpoint of the Alokai Middleware. In turn, in calls the
 * {@link @vsf-enterprise/sapcc-api#MiddlewareOAuthConfig#tokenRevokeEndpoint | token revoke endpoint}
 * of the {@link @vsf-enterprise/sapcc-api#MiddlewareOAuthConfig | SAP OAuth server} to
 * invalidate the access token stored as the `vsf-sap-token` browser cookie. It also removes the
 * cookie automatically afterwards.
 *
 * You might also want to see the {@link signUserIn | signUserIn} method.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * This method does not return anything.
 *
 * @example
 * Signing a user out.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const tokenResponse = await sdk.commerce.signUserOut();
 * ```
 */
async function signUserOut(options) {
    return new AxiosRequestSender(client)
        .setUrl("OAuthUserRevoke")
        .setMethod("POST")
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method refreshing an authenticated user token in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.OAuthUserTokenRefresh | OAuthUserTokenRefresh}
 * endpoint of the Alokai Middleware. In turn, it sends a request to the
 * {@link @vsf-enterprise/sapcc-api#MiddlewareOAuthConfig#tokenEndpoint | token endpoint}
 * of the {@link @vsf-enterprise/sapcc-api#MiddlewareOAuthConfig | SAP OAuth server}
 * to refresh the access token stored as the `vsf-sap-token` browser cookie. The refreshed
 * access token is saved in the cookie automatically using the
 * {@link https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Set-Cookie | Set-Cookie}
 * response header. The default options of the cookie can be modified through the
 * {@link @vsf-enterprise/sapcc-api#MiddlewareOAuthConfig#cookieOptions | cookieOptions}
 * property in `middleware.config.js`.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of an OAuthUserTokenResponse.
 *
 * @example
 * Refreshing a user token.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const tokenResponse = await sdk.commerce.refreshUserToken();
 * ```
 */
async function refreshUserToken(options) {
    return new AxiosRequestSender(client)
        .setUrl("OAuthUserTokenRefresh")
        .setMethod("POST")
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method updating a customer profile in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.updateUser | updateUser}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Users/updateUser | updateUser}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Users | Users Controller}.
 *
 * This method works only in an authenticated user session (i.e. when the `vsf-sap-token` cookie is present in the browser).
 * It only updates the fields provided in the {@link @vsf-enterprise/sapcc-types#UpdateUserProps.user | user} param.
 *
 * SAP allows to update only a subset of fields mentioned in the endpoint's payload interface.
 * Namely: firstName, lastName, titleCode, currency, language, sitePreference. If you want to overwrite other fields
 * then adjust SAP's source code to unlock them. Uid (nickname/email) is an exception here, to update it you can use {@link updateUserLogin | updateUserLogin } endpoint of our API.
 *
 * Bear in mind the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Users/updateUser | updateUser}
 * endpoint does not allow for updating customer {@link @vsf-enterprise/sap-commerce-webservices-sdk#User.uid | uid} (login) and so does not
 * this method.
 *
 * You might also want to see the {@link updateAndGetUser | updateAndGetUser} method.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * This method does not return anything.
 *
 * @example
 * Updating a customer profile.
 *
 * ```ts
 * import { UpdateUserProps } from '@vsf-enterprise/sapcc-types';
 * import { sdk } from '~/sdk';
 *
 * const props: UpdateUserProps = {
 *   user: {
 *     firstName: 'John',
 *     lastName: 'Doe'
 *   }
 * }
 *
 * await sdk.commerce.updateUser(props);
 * ```
 */
async function updateUser(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("updateUser")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method updating a customer profile and returning it from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.updateAndGetUser | updateAndGetUser}
 * endpoint of the Alokai Middleware which composes responses from two other endpoints:
 * {@link @vsf-enterprise/sapcc-api#Endpoints.updateUser | updateUser} and
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getUser | getUser}.
 * In turn, it exchanges data with two endpoints exposed by the SAP OCC API:
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Users/updateUser | updateUser}
 * and {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Users/getUser | getUser}.
 *
 * This method works only in an authenticated user session (i.e. when the `vsf-sap-token` cookie is present in the browser).
 * It only updates the fields provided in the {@link @vsf-enterprise/sapcc-types#UpdateUserProps.user | user} param.
 *
 * SAP allows to update only a subset of fields mentioned in the endpoint's payload interface.
 * Namely: firstName, lastName, titleCode, currency, language, sitePreference. If you want to overwrite other fields
 * then adjust SAP's source code to unlock them. Uid (nickname/email) is an exception here, to update it you can use {@link updateUserLogin | updateUserLogin } endpoint of our API.
 *
 * Bear in mind the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Users/updateUser | updateUser}
 * endpoint does not allow for updating customer {@link @vsf-enterprise/sap-commerce-webservices-sdk#User.uid | uid} (login) and so does not
 * this method.
 *
 * You might also want to see the {@link updateUser | updateUser} method.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a User.
 *
 * @example
 * Updating a customer profile.
 *
 * ```ts
 * import { UpdateAndGetUserProps } from '@vsf-enterprise/sapcc-types';
 * import { sdk } from '~/sdk';
 *
 * const props: UpdateAndGetUserProps = {
 *   user: {
 *     firstName: 'John',
 *     lastName: 'Doe'
 *   }
 * }
 *
 * const updatedUser = await sdk.commerce.updateAndGetUser(props);
 * ```
 *
 * @example
 * Updating a customer profile and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { UpdateAndGetUserProps } from '@vsf-enterprise/sapcc-types';
 * import { sdk } from '~/sdk';
 *
 * const props: UpdateAndGetUserProps = {
 *   user: {
 *     firstName: 'John',
 *     lastName: 'Doe'
 *   },
 *   fields: 'firstName,lastName'
 * }
 *
 * const updatedUser = await sdk.commerce.updateAndGetUser(props);
 * ```
 */
async function updateAndGetUser(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("updateAndGetUser")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method changing a customer password in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.changePassword | changePassword}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Users/replaceUserPassword | replaceUserPassword}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Users | Users Controller}.
 *
 * This method works only in an authenticated user session (i.e. when the `vsf-sap-token` cookie is present in the browser). The new password
 * must be at least six characters long as well as contain a special character (e.g "$"), an uppercase character and a number.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * This method does not return anything.
 *
 * @example
 * Updating a customer password.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * await sdk.commerce.changeUserPassword({
 *   old: 'oldPassword1!',
 *   new: 'newPassword1!'
 * });
 * ```
 */
async function changeUserPassword(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("changePassword")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method fetching customers addresses from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getAddresses | getAddresses}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Address/getAddresses | getAddresses}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Address | Address Controller}.
 *
 * The method fetches customer addresses based on the `vsf-sap-token` cookie (which is assigned to that customer).
 * The cookie must be present in the browser for the method to work properly.
 *
 * You might also want to see the {@link getUserAddress | getUserAddress} method.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of an Address List.
 *
 * @example
 * Fetching customer addresses.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const { addresses } = await sdk.commerce.getUserAddresses();
 * ```
 *
 * @example
 * Fetching customer addresses and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const { addresses } = await sdk.commerce.getUserAddresses({ fields: 'addresses(firstName,lastName)' });
 * ```
 */
async function getUserAddresses(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getAddresses")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method creating a new customer address in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.createAddress | createAddress}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Address/createAddress | createAddress}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Address | Address Controller}.
 *
 * The method creates an address for a customer based on the `vsf-sap-token` cookie (which is assigned to that customer).
 * The cookie must be present in the browser for the method to work properly.
 *
 * SAP Commerce's doesn't allow setting "district" field in the newly created address.
 * If you want to find workaround for this problem, check {@link https://docs.vuestorefront.io/integrations/sapcc/getting-started/supported-features#limitations-of-sap-commerce | Limitations page} in our documentation.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of an Address.
 *
 * @example
 * Creating a customer address.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 * import { CreateAddressProps } from '@vsf-enterprise/sapcc-types';
 *
 * const props: CreateAddressProps = {
 *   address: {
 *     firstName: 'Integration',
 *     lastName: 'Test',
 *     line1: 'Test',
 *     postalCode: '54-025',
 *     titleCode: 'mr',
 *     country: { isocode: 'GB' },
 *     town: 'Radom'
 *   }
 * };
 *
 * const address = await sdk.commerce.createUserAddress(props);
 * ```
 *
 * @example
 * Creating a customer address and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 * import { CreateAddressProps } from '@vsf-enterprise/sapcc-types';
 *
 * const props: CreateAddressProps = {
 *   address: {
 *     firstName: 'Integration',
 *     lastName: 'Test',
 *     line1: 'Test',
 *     postalCode: '54-025',
 *     titleCode: 'mr',
 *     country: { isocode: 'GB' },
 *     town: 'Radom'
 *   },
 *   fields: 'firstName,lastName'
 * };
 *
 * const address = await sdk.commerce.createUserAddress(props);
 * ```
 */
async function createUserAddress(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("createAddress")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method deleting a customer address in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.deleteAddress | deleteAddress}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Address/removeAddress | removeAddress}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Address | Address Controller}.
 *
 * The method deletes a customer address based on the `vsf-sap-token` cookie (which is assigned to that customer).
 * The cookie must be present in the browser for the method to work properly.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * This method does not return anything.
 *
 * @example
 * Deleting a customer address.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * await sdk.commerce.deleteUserAddress({ addressId: '8796879552535' });
 * ```
 */
async function deleteUserAddress(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("deleteAddress")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method updating an existing customer address in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.updateAddress | updateAddress}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Address/updateAddress | updateAddress}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Address | Address Controller}.
 *
 * This method works only in an authenticated user session (i.e. when the `vsf-sap-token` cookie is present in the browser).
 * It only updates the fields provided in the {@link @vsf-enterprise/sapcc-types#UpdateAddressProps.address | address} param.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * This method does not return anything.
 *
 * @example
 * Updating a customer address.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * await sdk.commerce.updateUserAddress({
 *   addressId: '8796879552535',
 *   address: {
 *     line1: 'Line 1'
 *   }
 * });
 * ```
 */
async function updateUserAddress(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("updateAddress")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method fetching a customer address from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getAddress | getAddress}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Address/getAddress | getAddress}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Address | Address Controller}.
 *
 * The method fetches customer address based on the `vsf-sap-token` cookie (which is assigned to that customer).
 * The cookie must be present in the browser for the method to work properly.
 *
 * You might also want to see the {@link getUserAddresses | getUserAddresses} method.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of an Address.
 *
 * @example
 * Fetching a customer address.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const address = await sdk.commerce.getUserAddress({ addressId: '8796879552535' });
 * ```
 *
 * @example
 * Fetching a customer address and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const address = await sdk.commerce.getUserAddress({
 *   addressId: '8796879552535',
 *   fields: 'firstName,lastName'
 * });
 * ```
 */
async function getUserAddress(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getAddress")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method fetching all customer carts from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getCarts | getCarts}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Carts/getCarts | getCarts}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Carts | Carts Controller}.
 *
 * The method fetches customer carts based on the `vsf-sap-token` cookie (which is assigned to that customer).
 * The cookie must be present in the browser for the method to work properly.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Cart List.
 *
 * @example
 * Fetching a list of all customer carts.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const { carts } = await sdk.commerce.getUserCarts();
 * ```
 *
 * @example
 * Fetching a list of customer carts and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const { carts } = await sdk.commerce.getUserCarts({ fields: 'carts(code)' });
 * ```
 *
 * @example
 * Fetching a list of saved customer carts.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const { carts } = await sdk.commerce.getUserCarts({ savedCartsOnly: true });
 * ```
 *
 * @example
 * Fetching a paginated list of customer carts. The
 * {@link @vsf-enterprise/sapcc-types#GetAllCartsProps.currentPage | currentPage} and
 * {@link @vsf-enterprise/sapcc-types#GetAllCartsProps.pageSize | pageSize}
 * properties have effect only when
 * {@link @vsf-enterprise/sapcc-types#GetAllCartsProps.savedCartsOnly | savedCartsOnly}
 * is `true`. Their default values are `0` and `20` respectively.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const { carts } = await sdk.commerce.getUserCarts({
 *   savedCartsOnly: true,
 *   pageSize: 3,
 *   currentPage: 2
 * });
 * ```
 */
async function getUserCarts(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getCarts")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method fetching all customer orders placed for a specific Base Store in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getUserOrderHistory | getUserOrderHistory}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Orders/getUserOrderHistory | getUserOrderHistory}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Orders | Orders Controller}.
 *
 * The method fetches an order history based on the `vsf-sap-token` cookie (which is assigned to that customer).
 * The cookie must be present in the browser for the method to work properly.
 *
 * You might also want to see the {@link getUserOrder | getUserOrder} method.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of an Order History List.
 *
 * @example
 * Fetching an order history for the currently logged-in customer.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const orderHistory = await sdk.commerce.getUserOrderHistory();
 * ```
 *
 * @example
 * Fetching an order history for the currently logged-in customer and selecting
 * response {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const orderHistory = await sdk.commerce.getUserOrderHistory({
 *   fields: 'orders(code,status)'
 * });
 * ```
 *
 * @example
 * Fetching a paginated order history for the currently logged-in customer.
 * The default values for the
 * {@link @vsf-enterprise/sapcc-types#GetUserOrderHistoryProps.pageSize | pageSize} and
 * {@link @vsf-enterprise/sapcc-types#GetUserOrderHistoryProps.currentPage | currentPage}
 * parameters are `0` and `20` respectively.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const orderHistory = await sdk.commerce.getUserOrderHistory({
 *   pageSize: 3,
 *   currentPage: 1
 * });
 * ```
 *
 * @example
 * Fetching a filtered order history for the currently logged-in customer.
 * The {@link @vsf-enterprise/sapcc-types#GetUserOrderHistoryProps#statuses | statuses}
 * param is a comma-separated list of available
 * {@link @vsf-enterprise/sapcc-types#ORDER_STATUSES | order statuses}.
 *
 * ```ts
 * import { ORDER_STATUSES } from '@vsf-enterprise/sapcc-types;
 * import { sdk } from '~/sdk';
 *
 * const { CANCELLED, CHECKED_VALID } = ORDER_STATUSES;
 *
 * const orderHistory = await sdk.commerce.getUserOrderHistory({
 *   statuses: `${CANCELLED},${CHECKED_VALID}`
 * });
 * ```
 *
 * @example
 * Fetching a sorted order history for the currently logged-in customer.
 * By default, the results are sorted `byDate`.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const orderHistory = await sdk.commerce.getUserOrderHistory({
 *   sort: 'byOrderNumber'
 * });
 * ```
 */
async function getUserOrderHistory(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getUserOrderHistory")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method fetching a list of all consent templates from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getAllConsents | getAllConsents}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Consents/getConsentTemplates | getConsentTemplates}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Consents | Consents Controller}.
 *
 * When `vsf-sap-token` cookie is present in the browser, this method will fetch
 * consent templates for the current user. In case there is no cookie, it will fetch them for an anonymous
 * user.
 *
 * You might also want to see the {@link getConsentTemplate | getConsentTemplate} method.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Consent Template List.
 *
 * @example
 * Fetching a list of consent templates.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const { consentTemplates } = await sdk.commerce.getConsentTemplates();
 * ```
 *
 * @example
 * Fetching a list of consent templates and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const { consentTemplates } = await sdk.commerce.getConsentTemplates({
 *   fields: 'consentTemplates(id,name)'
 * });
 * ```
 */
async function getConsentTemplates(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getAllConsents")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method giving consent on behalf of an authenticated user in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.giveConsent | giveConsent}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Consents/doGiveConsent | doGiveConsent}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Consents | Consents Controller}.
 *
 * The method gives consent on behalf of an authenticated user based on the `vsf-sap-token` cookie (which is assigned to that customer).
 * The cookie must be present in the browser for the method to work properly.
 *
 * You might also want to see the {@link removeConsent | removeConsent} method.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Consent Template.
 *
 * @example
 * Giving consent.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const givenConsent = await sdk.commerce.giveConsent({
 *   consentTemplateId: 'MARKETING_NEWSLETTER',
 *   consentTemplateVersion: 0
 * });
 * ```
 */
async function giveConsent(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("giveConsent")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method withdrawing consent on behalf of an authenticated user in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.removeConsent | removeConsent}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Consents/removeConsent | removeConsent}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Consents | Consents Controller}.
 *
 * The method removes consent on behalf of an authenticated user based on the `vsf-sap-token` cookie (which is assigned to that customer).
 * The cookie must be present in the browser for the method to work properly.
 *
 * You might also want to see the {@link giveConsent | giveConsent} method.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @returns
 * This method does not return anything.
 *
 * @example
 * Withdrawing consent with a given
 * {@link @vsf-enterprise/sap-commerce-webservices-sdk#Consent.code | consentCode}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * await sdk.commerce.removeConsent({ consentCode: '000007PU' });
 * ```
 */
async function removeConsent(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("removeConsent")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method fetching a single consent template from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getConsent | getConsent}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Consents/getConsentTemplate | getConsentTemplate}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Consents | Consents Controller}.
 *
 * When `vsf-sap-token` cookie is present in the browser, this method will fetch a
 * consent template with given id for the current user. In case there is no cookie,
 * it will fetch it for an anonymous user.
 *
 * You might also want to see the {@link getConsentTemplates | getConsentTemplates} method.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Consent Template.
 *
 * @example
 * Fetching a consent template.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const consentTemplate = await sdk.commerce.getConsentTemplate({
 *   consentTemplateId: 'MARKETING_NEWSLETTER'
 * });
 * ```
 *
 * @example
 * Fetching a consent template and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const consentTemplate = await sdk.commerce.getConsentTemplate({
 *   consentTemplateId: 'MARKETING_NEWSLETTER',
 *   fields: 'id,name,description'
 * });
 * ```
 *
 */
async function getConsentTemplate(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getConsent")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method that updates an existing customer login (email) in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.updateUserLogin | updateUserLogin}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Users/replaceUserLogin | replaceUserLogin}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Users | Users Controller}.
 *
 * This method works only in an authenticated user session (i.e. when the `vsf-sap-token` cookie is present in the browser).
 * The provided new login has to be a valid email address. The provided password has to be the current user's password.
 *
 * Once the method is used, the customer has to sign in again with the new login to obtain a new access token.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * This method does not return anything.
 *
 * @example
 * Updating a customer login.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * await sdk.commerce.updateUserLogin({
 *   newLogin: 'newlogin@gmail.com',
 *   password: 'test1234',
 * });
 * ```
 */
async function updateUserLogin(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("updateUserLogin")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method used to cancel an order, completely or partially.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.cancelOrder | cancelOrder}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/Orders/doCancelOrder | doCancelOrder}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Orders | Orders Controller}.
 *
 * This method will only work for an registered user.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of an Order.
 *
 * @example
 * Cancelling an order from a cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * await sdk.commerce.cancelOrder({
 *  code: 'order-code',
 *  entries: {
 *   cancellationRequestEntryInputs: [
 *     {
 *       orderEntryNumber: 0,
 *       quantity: 1,
 *     },
 *   ],
 *  },
 * });
 * ```
 */
async function cancelOrder(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("cancelOrder")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Creates a registration request for a B2B user.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.createRegistrationRequest | createRegistrationRequest}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Users/createRegistrationRequest | createRegistrationRequest}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/B2B%20Users | B2b Users Controller}.
 *
 * This method will work only if B2B User extension is enabled.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of an Order.
 *
 * @example
 * Cancelling an order from a cart.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const b2bUserData: createRegistrationRequestProps = {
 *  orgUserRegistrationData: {
 *    titleCode: "mr",
 *    firstName: "john",
 *    lastName: "doe",
 *    email: "john+doe@integration-test.com",
 *    message:
 *      "Please create a new account for me. Department: Ground support; Position: Chief safeguard; Report to: Steve Jackson.",
 *  },
 *  sapCommerceCloudCaptchaToken: "token", // optional recaptcha token, when the captchaCheckEnabled = true for the store
 * };
 *
 * await sdk.commerce.createRegistrationRequest(b2bUserData);
 * ```
 */
async function createRegistrationRequest(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("createRegistrationRequest")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Fetch a B2B customer profile from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getOrgUser | getOrgUser}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Users/getOrgUser | getOrgUser}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/B2B%20Users | Users Controller}.
 *
 * The method fetches a customer profile based on the `vsf-sap-token` cookie (which is assigned to that customer).
 * The cookie must be present in the browser for the method to work properly.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a User.
 *
 * @example
 * Fetching a customer profile.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const user = await sdk.commerce.getOrgUser();
 * ```
 *
 * @example
 * Fetching a customer profile and selecting response
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const user = await sdk.commerce.getOrgUser({ fields: 'uid,firstName' });
 * ```
 */
async function getOrgUser(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getOrgUser")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Fetch details of a single product from SAP Commerce Cloud in the B2B channel.
 *
 * @remarks
 * This method sends a GET request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getOrgProduct | getOrgProduct}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Products/getOrgProduct | getOrgProduct}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/B2B%20Products | B2b Products Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Product.
 *
 * @example
 * Fetching a product by its code
 * (passed as the {@link @vsf-enterprise/sapcc-types#ProductGetProps.id | id} parameter).
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const product = await sdk.commerce.getOrgProduct({ id: '1992695' });
 * ```
 *
 * @example
 * Using the method the way it will be possible to cache the response.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const product = await sdk.commerce.getOrgProduct({ id: '1992695', lang: 'en', currency: 'GBP' });
 * ```
 *
 * @example
 * Fetching a product and selecting response {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const product = await sdk.commerce.getOrgProduct({ id: '1992695', fields: 'BASIC' });
 * ```
 */
async function getOrgProduct(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getOrgProduct")
        .setMethod("GET")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method fetching a list of products filtered by category and additional filters.
 *
 * @remarks
 * This method sends a GET request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getProductsByCategory | getProductsByCategory}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Categories/getProductsByCategory | getProductsByCategory}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/B2B%20Categories | B2B Categories Controller}.
 *
 * In the examples below, you can see how various parameter configurations
 * passed to the SDK method affect the final query parameter sent to the
 * SAP OCC API.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Product Search Page. That includes products and additional data,
 * such as available facets, sorting and pagination options. It can also include spelling suggestions.
 *
 * @example
 * Using the method without any parameters to fetch unfiltered
 * products and additional data.
 *
 * Final query: `:undefined`
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const result = await sdk.commerce.getProductsByCategory({ categoryId: '1'});
 * ```
 *
 * @example
 * Fetching paginated products and additional data
 * by using the {@link @vsf-enterprise/sapcc-types#GetProductsByCategoryProps.pageSize | pageSize}
 * and {@link @vsf-enterprise/sapcc-types#GetProductsByCategoryProps.currentPage | currentPage} parameters.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const result = await sdk.commerce.getProductsByCategory({ categoryId: '1', lang: 'en', currency: 'GBP', pageSize: 10, currentPage: 1 });
 * ```
 *
 * @example
 * Performing a free-text search and sorting the results by using
 *
 * ```ts
 * import { sdk } from '~/sdk';
 * const query = 'sneakers:topRated';
 * const result = await sdk.commerce.getProductsByCategory({ categoryId: '1', query, sort: 'topRated' });
 * ```
 *
 * @example
 * Performing a free-text search and selecting response fields by using
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields} parameters. The response object will
 * only contain the freeTextSearch key (alongside type and pagination
 * which are always included - regardless of specified
 * {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}).
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const query = `sneakers:undefined`;
 * const result = await sdk.commerce.getProductsByCategory({ categoryId: '1', query, fields: 'freeTextSearch' });
 * ```
 */
async function getProductsByCategory(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getProductsByCategory")
        .setMethod("GET")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Fetches a B2B cart of the current user.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getCurrentOrgCart | getCurrentOrgCart}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Carts/getCurrentOrgCart | getCurrentOrgCart}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Carts | B2B Carts Controller}.
 *
 * This method is only for an authenticated customer as it uses `vsf-sap-token` cookie,
 * to fetch a current B2B cart of the current user. In case there is no cookie, it won't work.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Cart.
 *
 * @example
 * Fetching a current B2B cart (make sure you are an authenticated customer).
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cart = await sdk.commerce.getCurrentOrgCart();
 * ```
 *
 * @example
 * Fetching a current B2B cart and selecting response {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cart = await sdk.commerce.getCurrentOrgCart({ fields: 'code,guid,user(FULL)' });
 * ```
 */
async function getCurrentOrgCart(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getCurrentOrgCart")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Assings an existing delivery address to the B2B cart in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.replaceOrgCartDeliveryAddress | replaceOrgCartDeliveryAddress}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Carts/replaceOrgCartDeliveryAddress | replaceOrgCartDeliveryAddress}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Carts | B2B Carts Controller}.
 *
 * This method is only for an authenticated customer
 * as it uses `vsf-sap-token` cookie.
 * In case there is no cookie, it won't work.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Cart.
 *
 * @example
 * Assigning an existing delivery address to the B2B cart (make sure you are an authenticated customer).
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cart = await sdk.commerce.replaceCartAddress({ cartId: '00035084', addressId: '8796716793879' });
 * ```
 *
 * @example
 * Assigning an existing delivery address to the B2B cart and selecting response {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cart = await sdk.commerce.replaceCartAddress({ cartId: '00035084', addressId: '8796716793879', fields: 'code,guid,user(FULL)' });
 * ```
 */
async function replaceOrgCartDeliveryAddress(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("replaceOrgCartDeliveryAddress")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Updates the payment type for the B2B cart in SAP Commerce Cloud.
 * Optionally, it updates purchaseOrderNumber if it's present.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.replaceOrgCartPaymentType | replaceOrgCartPaymentType}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Carts/replaceOrgCartPaymentType | replaceOrgCartPaymentType}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Carts | B2B Carts Controller}.
 *
 * This method is only for an authenticated customer
 * as it uses `vsf-sap-token` cookie.
 * In case there is no cookie, it won't work.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Cart.
 *
 * @example
 * Updates the payment type for the B2B cart (make sure you are an authenticated customer).
 * HINT: Fetch available payment types using getPaymentTypes SDK method.
 * Then pass `code` property of selected Payment Type object from the array.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cart = await sdk.commerce.replaceOrgCartPaymentType({ cartId: '00035084', paymentType: 'ACCOUNT' });
 * ```
 *
 * @example
 * Updates the payment type for the B2B cart and selecting response {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cart = await sdk.commerce.replaceOrgCartPaymentType({ cartId: '00035084', paymentType: 'ACCOUNT', fields: 'code,guid,user(FULL)' });
 * ```
 *
 * @example
 * Updates the payment type and purchase order number for the B2B cart (make sure you are an authenticated customer).
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cart = await sdk.commerce.replaceOrgCartPaymentType({ cartId: '00035084', paymentType: 'ACCOUNT', purchaseOrderNumber: '567213' });
 * ```
 */
async function replaceOrgCartPaymentType(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("replaceOrgCartPaymentType")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Updates the cost center for the B2B cart in SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.replaceOrgCartCostCenter | replaceOrgCartCostCenter}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Carts/replaceOrgCartCostCenter | replaceOrgCartCostCenter}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Carts | B2B Carts Controller}.
 *
 * This method is only for an authenticated customer
 * as it uses `vsf-sap-token` cookie.
 * In case there is no cookie, it won't work.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a Cart.
 *
 * @example
 * Updates the cost center for the B2B cart (make sure you are an authenticated customer).
 * HINT: Before, make sure you set added some entity to the cart and set the payment type
 * to the ACCOUNT.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cart = await sdk.commerce.replaceOrgCartCostCenter({ cartId: '00035084', costCenterId: 'Pronto_Services' });
 * ```
 *
 * @example
 * Updates the cost center for the B2B cart and selecting response {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cart = await sdk.commerce.replaceOrgCartCostCenter({ cartId: '00035084', costCenterId: 'Pronto_Services', fields: 'code,guid,costCenter(FULL)' });
 * ```
 */
async function replaceOrgCartCostCenter(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("replaceOrgCartCostCenter")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Replace entries in the B2B cart
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.replaceOrgCartEntries | replaceOrgCartEntries }
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Carts/replaceOrgCartEntries | replaceOrgCartEntries }
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Carts | B2B Carts Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns cart modification list
 *
 * @example
 * Replacing entries in the B2B cart
 *
 * ```ts
 * const { cartModifications } = await sdk.commerce.replaceOrgCartEntries({
 *   cartId: "00035084",
 *   orderEntryList: {
 *     orderEntries: [
 *       {
 *          quantity: 3,
 *          product: {
 *            code: "3881017"
 *          }
 *       }
 *     ]
 *   }
 * });
 * ```
 *
 * @example
 * Replacing entries in the B2B cart and selecting response {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * const { cartModifications } = await sdk.commerce.replaceOrgCartEntries({
 *   cartId: "00035084",
 *   fields: 'cartModifications(statusCode)',
 *   orderEntryList: {
 *     orderEntries: [
 *       {
 *          quantity: 3,
 *          product: {
 *            code: "3881017"
 *          }
 *       }
 *     ]
 *   }
 * });
 * ```
 */
async function replaceOrgCartEntries(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("replaceOrgCartEntries")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Add entries to a B2B cart
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.doAddOrgCartEntries | doAddOrgCartEntries }
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Carts/doAddOrgCartEntries | doAddOrgCartEntries }
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Carts | B2B Carts Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns cart modification list
 *
 * @example
 * Adding entries to a B2B cart
 *
 * ```ts
 * const doAddOrgCartEntries = await sdk.commerce.doAddOrgCartEntries({
 *   cartId: "00035084",
 *   orderEntryList: {
 *     orderEntries: [
 *       {
 *          quantity: 1,
 *          product: {
 *            code: "3881017"
 *          }
 *       }
 *     ]
 *   }
 * });
 * ```
 */
async function doAddOrgCartEntries(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("doAddOrgCartEntries")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Fetches a list of order approvals on the B2B Commerce Cloud instance.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getOrderApprovals | getOrderApprovals}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/Order%20Approvals/getOrderApprovals | getOrderApprovals}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/Order%20Approvals | B2B Order Approvals controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a OrderApprovalList.
 *
 * @example
 * Fetching order approvals.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const approvals = await sdk.commerce.getOrderApprovals();
 * ```
 *
 * @example
 * Fetching order approvals with additional parameters.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const approvals = await sdk.commerce.getOrderApprovals({ currentPage: 1, pageSize: 10, sort: "byOrderNumber" });
 * ```
 */
async function getOrderApprovals(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getOrderApprovals")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Fetches a list of order approvals on the B2B Commerce Cloud instance.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getOrderApproval | getOrderApproval}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/Order%20Approvals/getOrderApproval | getOrderApproval}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/Order%20Approvals | B2B Order Approval controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a OrderApproval.
 *
 * @example
 * Fetching order approvals.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const approvals = await sdk.commerce.getOrderApproval({ orderApprovalCode: "approval-code"});
 * ```
 */
async function getOrderApproval(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getOrderApproval")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Creates a decision (approval or rejection) for the order, which will trigger the next step in the approval workflow.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.doMakeOrderApprovalDecision | doMakeOrderApprovalDecision}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/Order%20Approvals/doMakeOrderApprovalDecision | doMakeOrderApprovalDecision}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/Order%20Approvals | B2B Order Approval controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a OrderApproval.
 *
 * @example
 * Fetching order approvals.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const result = await sdk.commerce.doMakeOrderApprovalDecision(
 *  {
 *    orderApprovalCode: "approval-code",
 *    orderApprovalDecision: { decision: "REJECTED", comment: "test-comment" }
 *  }
 * );
 * ```
 */
async function doMakeOrderApprovalDecision(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("doMakeOrderApprovalDecision")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Fetch Active Cost Centers list from SAP Commerce Cloud in the B2B channel.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getActiveCostCenters | getActiveCostCenters}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Cost%20Centers/getActiveCostCenters | getActiveCostCenters}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/B2B%20Cost%20Centers | B2B Cost Centers Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a B2BCostCenterList.
 *
 * @example
 * Fetching active cost centers with optional parameters
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const costCenters = await sdk.commerce.getActiveCostCenters({ lang: 'en', currency: 'GBP' });
 * ```
 */
async function getActiveCostCenters(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getActiveCostCenters")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method that gets the payment types available to the user's B2B organization
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getPaymentTypes | getPaymentTypes }
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Miscs/getPaymentTypes}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Miscs | B2B Orders Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns payment types available to the user's B2B organization
 *
 * @example
 *
 * ```ts
 * const paymentTypes = await sdk.commerce.getPaymentTypes();
 * ```
 */
async function getPaymentTypes(props, options) {
    return (new AxiosRequestSender(client)
        .setUrl("getPaymentTypes")
        // the request requires a token, so we use POST so it doesn't get cached
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send());
}

/**
 * Method that creates a cart based on the products ordered in a past order
 * This method requires deleting the current cart, so that a new cart based on the given order can be put in its place.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.createCartFromOrder | createCartFromOrder}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Orders/createCartFromOrder | createCartFromOrder}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/B2B Orders | B2B Orders Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns the operations applied to the new cart in order to make it the same as from the one in input
 *
 * @example
 * Creating a new cart, which will contain products bought as part of a previous order
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const props = { orderCode: "00001234" };
 * await sdk.commerce.deleteCart({ cartId: "current"});
 * const cartModificationsList = await sdk.commerce.createCartFromOrder(props);
 * ```
 */
async function createCartFromOrder(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("createCartFromOrder")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method that creates a B2B replenishment order, based on a cart
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.createReplenishmentOrder | createReplenishmentOrder}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Orders/createReplenishmentOrder | createReplenishmentOrder}
 * endpoint exposed by the SAP OCC API
 * {@link https://spartacus-demo.eastus.cloudapp.azure.com:8443/occ/v2/swagger-ui/index.html#/B2B%20Orders Orders | B2B Orders Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns the newly created replenishment order
 *
 * @example
 * Placing a new replenishment order
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * // All the complex info like shipping/delivery address must already filled out in the cart entity
 * // see sdk.createCart()
 * const props = {
 *   cartId: "00001234",
 *   termsChecked: true,
 *   scheduleReplenishmentForm: {
 *     replenishmentStartDate: "2023-10-25T11:56:39Z",
 *     numberOfDays: "14",
 *     numberOfWeeks: "1",
 *     nthDayOfMonth: "1",
 *     recurrencePeriod: "WEEKLY",
 *     daysOfWeek: ["SUNDAY"]
 *   }
 * };
 *
 * const newReplenishmentOrder = await sdk.commerce.createReplenishmentOrder(props);
 * ```
 */
async function createReplenishmentOrder(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("createReplenishmentOrder")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method that gets a single B2B order from the user's organization's branch
 *
 * Make sure that the B2B user calling this method is in the Unit Order Viewer group. This will make the user have the correct permissions to call this endpoint.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getBranchOrder | getBranchOrder}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Orders/getBranchOrder | getBranchOrder}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/B2B Orders | B2B Orders Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns one B2B order with the requested ID from the user's organization's unit
 *
 * @example
 * Requesting a single order from the user's organization's branch
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const props = { code: "00001234" };
 * const order = await sdk.commerce.getBranchOrder(props);
 * ```
 */
async function getBranchOrder(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getBranchOrder")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method that paginates through B2B order from the user's organization's branch
 *
 * Make sure that the B2B user calling this method is in the Unit Order Viewer group. This will make the user have the correct permissions to call this endpoint.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getUserBranchOrderHistory | getUserBranchOrderHistory}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Orders/getUserBranchOrderHistory | getUserBranchOrderHistory}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/B2B Orders | B2B Orders Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns paginated/filtered/sorted order history for the user's organization's branch
 *
 * @example
 * Getting the order history from the user's organization's branch
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * // All of the props below are optional
 * const props = {
 *   currentPage: 1,
 *   filters: '::user:someUserHere';
 *   pageSize: 10;
 *   sort: 'someSortingMethod';
 *   statuses: 'CANCELLED,CHECKED_VALID';
 * };
 * const orderHistory = await sdk.commerce.getUserBranchOrderHistory(props);
 * // or just
 * const orderHistory = await sdk.commerce.getUserBranchOrderHistory();
 * ```
 */
async function getUserBranchOrderHistory(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getUserBranchOrderHistory")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method that creates a B2B order, based on a cart
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.placeOrgOrder | placeOrgOrder}
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://spartacus-demo.eastus.cloudapp.azure.com:8443/occ/v2/swagger-ui/index.html#/B2B%20Orders/placeOrgOrder | placeOrgOrder}
 * endpoint exposed by the SAP OCC API
 * {@link https://spartacus-demo.eastus.cloudapp.azure.com:8443/occ/v2/swagger-ui/index.html#/B2B%20Orders Orders | B2B Orders Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns the newly created order
 *
 * @example
 * Placing a new order
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * // All the complex info like shipping/delivery address must already filled out in the cart entity
 * // see sdk.createCart()
 * const props = {
 *   cartId: "00001234",
 *   termsChecked: true,
 * }
 * const newOrder = await sdk.commerce.placeOrgOrder(props);
 * ```
 */
async function placeOrgOrder(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("placeOrgOrder")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Fetch cost center by the code list from SAP Commerce Cloud in the B2B channel.
 *
 * @remarks
 * This method sends a GET request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getCostCenter | getCostCenter}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Cost%20Centers/getCostCenter | getCostCenter}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/B2B%20Cost%20Centers | B2B Cost Centers Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a B2BCostCenter.
 *
 * @example
 * Fetching active cost centers with optional parameters
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const costCenter = await sdk.commerce.getCostCenter({ costCenterCode: "pronto_services" });
 * ```
 */
async function getCostCenter(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getCostCenter")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Fetch Active Cost Centers list from SAP Commerce Cloud in the B2B channel.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getCostCenters | getCostCenters}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Cost%20Centers/getCostCenters | getCostCenters}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/B2B%20Cost%20Centers | B2B Cost Centers Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a B2BCostCenterList.
 *
 * @example
 * Fetching active cost centers with optional parameters
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const costCenters = await sdk.commerce.getCostCenters({ sort: "byCode", pageSize: 10 });
 * ```
 */
async function getCostCenters(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getCostCenters")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Fetch all budgets and certain budgets associated with a specific cost center.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getBudgetsForCostCenterProps | getBudgetsForCostCenterProps}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/B2B%20Cost%20Centers/getBudgetsForCostCenterProps | getBudgetsForCostCenterProps}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/B2B%20Cost%20Centers | B2B Cost Centers Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a BudgetList.
 *
 * @example
 * Fetching active cost centers with optional parameters
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const costCenters = await sdk.commerce.getBudgetsForCostCenter({ costCenterCode: "some-code", sort: "byCode", pageSize: 10 });
 * ```
 */
async function getBudgetsForCostCenter(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getBudgetsForCostCenter")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method that gets physical points of service assigned to a customer support agent
 *
 * Be advised: If your version of SAP CC is lower than 2211.16, the underlying raw SAP CC endpoint for "getPointOfServices" is disabled by default. Set "commercewebservices.api.restrictions.disabled.endpoints" to "" in your manifest.json in your SAP CC config repository.
 *
 * @see {@link https://help.sap.com/docs/SAP_COMMERCE_CLOUD_PUBLIC_CLOUD/75d4c3895cb346008545900bffe851ce/9c617ae01b534677b49d01587a8c20f2.html | More info about commerceswebservices.api.restrictions.disabled.endpoints}
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getPointOfServices | getPointOfServices }
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/assistedservicewebservices/swagger-ui/index.html#/Agent%20Controller/getPointOfServices | getPointOfServices }
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/assistedservicewebservices/swagger-ui/index.html#/Agent%20Controller | Agent Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns list of points of sale assigned to given customer support agent
 *
 * @example
 * Requesting the currently logged in agent's assigned points of service
 *
 * ```ts
 * const getPointOfServices = await sdk.commerce.getPointOfServices({ agentId: 'current'});
 * ```

 * @example
 * Requesting another agent's assigned points of service
 *
 * ```ts
 * const getPointOfServices = await sdk.commerce.getPointOfServices({ agentId: 'customer.support@nakano.com'});
 * ```
 */
async function getPointOfServices(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getPointOfServices")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Retrieves autocomplete information on customers based on the customer query
 *
 * @remarks Be advised: If your version of SAP CC is lower than 2211.16, the underlying raw SAP CC endpoint for "getPointOfServices" is disabled by default. Set "commercewebservices.api.restrictions.disabled.endpoints" to "" in your manifest.json in your SAP CC config repository.
 * @see {@link https://help.sap.com/docs/SAP_COMMERCE_CLOUD_PUBLIC_CLOUD/75d4c3895cb346008545900bffe851ce/9c617ae01b534677b49d01587a8c20f2.html | More info about commerceswebservices.api.restrictions.disabled.endpoints}
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.autoComplete | autoComplete }
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/assistedservicewebservices/swagger-ui/index.html#/customers-controller/autoComplete | autoComplete }
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/assistedservicewebservices/swagger-ui/index.html#/customers-controller | Customer Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns array of customer suggestions
 *
 * @example
 * Requesting autocomplete information on customers based on the customer query
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const result = await sdk.commerce.autoComplete({ customerQuery: 'john'});
 * ```
 */
async function autoComplete(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("autoComplete")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Creates an account for a customer by assisted service agent.
 *
 * @remarks
 * Be advised: If your version of SAP CC is lower than 2211.16, the underlying raw SAP CC endpoint for "getPointOfServices" is disabled by default. Set "commercewebservices.api.restrictions.disabled.endpoints" to "" in your manifest.json in your SAP CC config repository.
 * @see {@link https://help.sap.com/docs/SAP_COMMERCE_CLOUD_PUBLIC_CLOUD/75d4c3895cb346008545900bffe851ce/9c617ae01b534677b49d01587a8c20f2.html | More info about commerceswebservices.api.restrictions.disabled.endpoints}
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.autoComplete | autoComplete }
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/assistedservicewebservices/swagger-ui/index.html#/customers-controller/createCustomer | createCustomer }
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/assistedservicewebservices/swagger-ui/index.html#/customers-controller | Customer Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a created customer.
 *
 * @example
 * Requesting autocomplete information on customers based on the customer query
 *
 * ```ts
 * const user = await sdk.commerce.createCustomer({
 *  customerRegistrationForm: {
 *    firstName: "Brande",
 *    lastName: "Smith",
 *    emailAddress: "brande.smith@sap.com",
 *  },
 * })
 *
 * // User contains object with customer data
 * ```
 */
async function createCustomer(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("createCustomer")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Retrieves the details of a customer list with a valid ID
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getCustomerListDetails | getCustomerListDetails }
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/assistedservicewebservices/swagger-ui/index.html#/customers-controller/getCustomerListDetails | getCustomerListDetails }
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/assistedservicewebservices/swagger-ui/index.html#/customers-controller | Customer Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns representation of a customer list
 *
 * @example
 * Requesting customer list details
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const list = await sdk.commerce.getCustomerListDetails({ customerlist: 'asmInstoreCustomerList'});
 * ```
 */
async function getCustomerListDetails(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getCustomerListDetails")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Creates a bind between the provided customer ID and an anonymous cart.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.bindCart | bindCart }
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/assistedservicewebservices/swagger-ui/index.html#/customers-controller/bindCart | bindCart }
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/assistedservicewebservices/swagger-ui/index.html#/customers-controller | Customer Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns array of customer suggestions
 *
 * @example
 * Binding a customer ID to an anonymous cart
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * await sdk.commerce.bindCart({ cartId: '123-123-123', customerId: 'john.doe@email.com'});
 * ```
 */
async function bindCart(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("bindCart")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Retrieves a list of all customer lists. This can only be done when logged in ASM.
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getCustomerLists | getCustomerLists }
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/assistedservicewebservices/swagger-ui/index.html#/customers-controller/getCustomerLists | getCustomerLists }
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/assistedservicewebservices/swagger-ui/index.html#/customers-controller | Customer Controller}.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns representation of a User Group List
 *
 * @example
 * Requesting a list of all customer lists
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const cutomerLists = await sdk.commerce.getCustomerLists();
 * ```
 */
async function getCustomerLists(options) {
    return new AxiosRequestSender(client)
        .setUrl("getCustomerLists")
        .setMethod("POST")
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method that gets customer data from Customer 360 in Assisted Service Module
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.searchCustomer360 | searchCustomer360 }
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/assistedservicewebservices/swagger-ui/index.html#/ASM%20Customer%20360/searchCustomer360 | searchCustomer360 }
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/assistedservicewebservices/swagger-ui/index.html#/ASM%20Customer%20360 | ASM Customer 360 Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns response of query to Customer 360
 *
 * @example
 * Requesting customer data from Customer 360
 *
 * ```ts
 * const searchCustomer360 = await sdk.commerce.searchCustomer360({
 *   userId: "generated-lenny70@yahoo.com",
 *   customer360QueryList: {
 *     customer360Queries: [
 *       { type: "c360Overview" }
 *     ]
 *    }
 * });
 * ```
 */
async function searchCustomer360(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("searchCustomer360")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Method that gets paginated list of customers for Assisted Service Module
 *
 * @remarks
 * This method sends a POST request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getPageableCustomers | getPageableCustomers }
 * endpoint of the Alokai Middleware. In turn, it exchanges data with the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/assistedservicewebservices/swagger-ui/index.html#/customers-controller/getPageableCustomers | getPageableCustomers }
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/assistedservicewebservices/swagger-ui/index.html#/customers-controller | Customers Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns page of customers
 *
 * @example
 * Requesting a customer page
 *
 * ```ts
 * const getPageableCustomers = await sdk.commerce.getPageableCustomers({
 *   currentPage: 1,
 *   customerListId: "asmInstoreCustomerList",
 *   pageSize: 5,
 * });
 * ```
 */
async function getPageableCustomers(props = {}, options = {}) {
    return new AxiosRequestSender(client)
        .setUrl("getPageableCustomers")
        .setMethod("POST")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

/**
 * Fetch details of a list of products from SAP Commerce Cloud.
 *
 * @remarks
 * This method sends a GET request to the
 * {@link @vsf-enterprise/sapcc-api#Endpoints.getProductsByIds | getProductsByIds}
 * endpoint of the Alokai Middleware. In turn, it receives data from the
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui.html#/Alokai/getProductsByIds | getProductsByIds}
 * endpoint exposed by the SAP OCC API
 * {@link https://api.c1jvi8hu9a-vsfspzooa1-d1-public.model-t.cc.commerce.ondemand.com/occ/v2/swagger-ui/index.html#/Alokai | Alokai Products Controller}.
 *
 * @param props
 * Parameter object which can be used with this method.
 * Refer to its type definition to learn about possible properties.
 *
 * @param { MethodOptions } options
 * Options that can be passed to additionally configure the request
 * or customize the logic in a plugin.
 *
 * @returns
 * Returns a representation of a list of Products.
 *
 * @example
 * Fetching products by their IDs
 * (passed as the {@link @vsf-enterprise/sapcc-types#ProductsByIdsProps.ids | ids} parameter).
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const products = await sdk.commerce.getProductsByIds({ ids: ['1992695', '4392695'] });
 * ```
 *
 * @example
 * Using the method the way it will be possible to cache the response.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const products = await sdk.commerce.getProductsByIds({ ids: ['1992695', '4392695'], lang: 'en', currency: 'GBP'  }); * ```
 *
 * @example
 * Fetching a product and selecting response {@link @vsf-enterprise/sapcc-types#BaseProps.fields | fields}.
 *
 * ```ts
 * import { sdk } from '~/sdk';
 *
 * const products = await sdk.commerce.getProductsByIds({ ids: ['1992695', '4392695'], fields: 'BASIC' });
 * ```
 */
async function getProductsByIds(props, options) {
    return new AxiosRequestSender(client)
        .setUrl("getProductsByIds")
        .setMethod("GET")
        .setProps(props)
        .setConfig(options?.axiosRequestConfig)
        .send();
}

// B2C

var methods = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getProduct: getProduct,
  getProductReferences: getProductReferences,
  getProductReviews: getProductReviews,
  getSearchSuggestions: getSearchSuggestions,
  createProductReview: createProductReview,
  searchProduct: searchProduct,
  getCategory: getCategory,
  getCatalogVersion: getCatalogVersion,
  getProductSearchPageData: getProductSearchPageData,
  getCountries: getCountries,
  getCountryRegions: getCountryRegions,
  getTitles: getTitles,
  getBaseStore: getBaseStore,
  createCart: createCart,
  getCart: getCart,
  deleteCart: deleteCart,
  saveCart: saveCart,
  cloneCart: cloneCart,
  restoreCart: restoreCart,
  addCartEntry: addCartEntry,
  addEntryAndGetCart: addEntryAndGetCart,
  removeCartEntry: removeCartEntry,
  removeEntryAndGetCart: removeEntryAndGetCart,
  updateCartEntry: updateCartEntry,
  updateEntryAndGetCart: updateEntryAndGetCart,
  addCartEmail: addCartEmail,
  createCartAddress: createCartAddress,
  removeCartAddress: removeCartAddress,
  replaceCartAddress: replaceCartAddress,
  getCartDeliveryModes: getCartDeliveryModes,
  replaceCartDeliveryMode: replaceCartDeliveryMode,
  createCartPaymentDetails: createCartPaymentDetails,
  replaceCartPaymentDetails: replaceCartPaymentDetails,
  addCartVoucher: addCartVoucher,
  addVoucherAndGetCart: addVoucherAndGetCart,
  removeCartVoucher: removeCartVoucher,
  placeOrder: placeOrder,
  getUserOrder: getUserOrder,
  createUser: createUser,
  getUser: getUser,
  signUserIn: signUserIn,
  signUserOut: signUserOut,
  refreshUserToken: refreshUserToken,
  updateUser: updateUser,
  updateAndGetUser: updateAndGetUser,
  changeUserPassword: changeUserPassword,
  getUserAddresses: getUserAddresses,
  createUserAddress: createUserAddress,
  deleteUserAddress: deleteUserAddress,
  updateUserAddress: updateUserAddress,
  getUserAddress: getUserAddress,
  getUserCarts: getUserCarts,
  getUserOrderHistory: getUserOrderHistory,
  getConsentTemplates: getConsentTemplates,
  giveConsent: giveConsent,
  removeConsent: removeConsent,
  getConsentTemplate: getConsentTemplate,
  updateUserLogin: updateUserLogin,
  cancelOrder: cancelOrder,
  createRegistrationRequest: createRegistrationRequest,
  getOrgUser: getOrgUser,
  getOrgProduct: getOrgProduct,
  getProductsByCategory: getProductsByCategory,
  getCurrentOrgCart: getCurrentOrgCart,
  replaceOrgCartDeliveryAddress: replaceOrgCartDeliveryAddress,
  replaceOrgCartPaymentType: replaceOrgCartPaymentType,
  replaceOrgCartCostCenter: replaceOrgCartCostCenter,
  replaceOrgCartEntries: replaceOrgCartEntries,
  doAddOrgCartEntries: doAddOrgCartEntries,
  getOrderApprovals: getOrderApprovals,
  getOrderApproval: getOrderApproval,
  doMakeOrderApprovalDecision: doMakeOrderApprovalDecision,
  getActiveCostCenters: getActiveCostCenters,
  getPaymentTypes: getPaymentTypes,
  createCartFromOrder: createCartFromOrder,
  createReplenishmentOrder: createReplenishmentOrder,
  getBranchOrder: getBranchOrder,
  getUserBranchOrderHistory: getUserBranchOrderHistory,
  placeOrgOrder: placeOrgOrder,
  getCostCenter: getCostCenter,
  getCostCenters: getCostCenters,
  getBudgetsForCostCenter: getBudgetsForCostCenter,
  getPointOfServices: getPointOfServices,
  autoComplete: autoComplete,
  createCustomer: createCustomer,
  getCustomerListDetails: getCustomerListDetails,
  bindCart: bindCart,
  getCustomerLists: getCustomerLists,
  searchCustomer360: searchCustomer360,
  getPageableCustomers: getPageableCustomers,
  getProductsByIds: getProductsByIds
});

/**
 * Initialize the SAP Commerce Cloud connector.
 */
const sapccConnector = (options) => {
    config.set("apiUrl", options.apiUrl);
    config.set("ssrApiUrl", options.ssrApiUrl);
    config.set("refreshToken", options.refreshToken);
    return methods;
};

/**
 * SAP Commerce Cloud module.
 *
 * @deprecated
 *
 * Use `middlewareModule` instead.
 *
 * ```diff
 * - import { initSDK, buildModule } from '@vue-storefront/sdk';
 * - import { sapccModule, SAPCCModuleType } from '@vsf-enterprise/sapcc-sdk'
 * + import { initSDK, buildModule, middlewareModule } from '@vue-storefront/sdk';
 * + import { Endpoints as SapccEndpoints } from '@vsf-enterprise/sapcc-api';
 *
 * const sdkConfig = {
 *   sapcc:
 *     buildModule(
 * -      sapccModule,
 * +      middlewareModule<SapccEndpoints>,
 *       {
 *         apiUrl: 'http://localhost:8181/sapcc',
 *         ssrApiUrl: 'http://localhost:8181/sapcc'
 *       }
 *     )
 * };
 *
 * export const sdk = initSDK(sdkConfig);
 *
 * // Breaking changes:
 *
 * - const cartModification = await sdk.sapcc.addCartEntry(params)
 * + const cartModification = await sdk.sapcc.addToCart(params)
 *
 * - const cartModification = await sdk.sapcc.updateCartEntry(params)
 * + const cartModification = await sdk.sapcc.updateCart(params)
 * ```
 *
 * @example
 *
 * Initialization of the SAP Commerce Cloud module.
 *
 * ```js
 * import { initSDK, buildModule } from '@vue-storefront/sdk';
 * import { sapccModule } from '@vsf-enterprise/sapcc-sdk'
 *
 * const sdkConfig = {
 *   sapcc:
 *     buildModule(
 *       sapccModule,
 *       {
 *         apiUrl: 'http://localhost:8181/sapcc',
 *         ssrApiUrl: 'http://localhost:8181/sapcc'
 *       }
 *     )
 * };
 *
 * export const sdk = initSDK(sdkConfig);
 * ```
 */
const sapccModule = (options) => ({
    connector: sapccConnector(options),
    utils: {},
    subscribers: {},
});

export { client, createRefreshTokenAndRetryHandler, sapccModule };
//# sourceMappingURL=index.es.js.map
